/**
 * BattleScene - Core chess battle gameplay
 * Implements turn-based chess combat with roguelike elements
 */

import Phaser from 'phaser';
import { ChessGame } from '../game/chess/ChessGame';
import { AudioManager } from '../game/AudioManager';
import { GameStateManager } from '../game/GameStateManager';
import {
  SCENES,
  GAME_CONFIG,
  BOARD_CONFIG,
  Z_DEPTHS,
  ANIMATION_DURATION,
  AI_DIFFICULTY,
} from '../constants/GameConstants';
import { Square, PieceData, FloorData, PieceType, Obstacle } from '../types';
import { ENCOUNTER_DATABASE, EncounterDefinition } from '../game/encounters/EncounterDatabase';
import { T, INK, PALETTE, paintBackground, fadeInScene, makeButton, makePanel } from '../constants/Theme';
import { ChessClock } from '../game/ChessClock';
import { generateEnemyArmy } from '../game/EnemyArmyGenerator';
import { CHESS_CLOCK_CONFIG } from '../constants/GameConstants';

export class BattleScene extends Phaser.Scene {
  private chessGame!: ChessGame;
  private boardGraphics!: Phaser.GameObjects.Graphics;
  private pieceSprites: Map<Square, Phaser.GameObjects.Sprite> = new Map();
  private highlightGraphics!: Phaser.GameObjects.Graphics;
  private markerGraphics!: Phaser.GameObjects.Graphics;
  private lastMove: { from: Square; to: Square } | null = null;
  private moveFx: Phaser.GameObjects.GameObject[] = [];
  private selectedSquare: Square | null = null;
  private validMoves: Square[] = [];
  private pieces: Map<Square, PieceData> = new Map();
  private originalPieceTypes: Map<Square, PieceType> = new Map(); // Track original types for pawn promotion reversion
  private repositionedPlayerPieces: PieceData[] = []; // Store the actual positioned pieces for initialization
  private isPlayerTurn = true;
  private isProcessingMove = false;
  private gameStateManager!: GameStateManager;
  private floorData!: FloorData;
  private encounter: EncounterDefinition | null = null;

  // UI Elements
  private turnText!: Phaser.GameObjects.Text;

  // Upgrade-related state
  private abilityButtons: Phaser.GameObjects.Container[] = [];
  private chessClock?: ChessClock;
  private clockText?: Phaser.GameObjects.Text;
  private enemyClockText?: Phaser.GameObjects.Text;
  private fogOverlay?: Phaser.GameObjects.Graphics;
  private teleporterGraphics: Phaser.GameObjects.Graphics[] = [];
  private stunIndicators: Map<string, Phaser.GameObjects.Text> = new Map();
  private playerTurnNumber: number = 0;
  private isExtraMoveActive: boolean = false;
  private extraMovePiece: Square | null = null;

  // Endless-game guards: quiet halfmoves (no capture/pawn move) and position repetition
  private quietHalfmoves: number = 0;
  private positionCounts: Map<string, number> = new Map();

  // Player-facing message toast
  private messageText?: Phaser.GameObjects.Text;
  private messageTween?: Phaser.Tweens.Tween;

  // Obstacle-moving mode state
  private obstacleMode: 'none' | 'select_wall' | 'place_wall' | 'select_water' | 'place_water' | 'sacrifice_select' = 'none';
  private selectedWallSquare: string | null = null;
  private selectedWallDirection: string | null = null;
  private obstacleGraphicsObjects: Phaser.GameObjects.GameObject[] = [];
  private cancelTargetingButton?: Phaser.GameObjects.Container;

  constructor() {
    super({ key: SCENES.BATTLE });
  }

  init(data: { floorData?: FloorData } = {}): void {
    this.gameStateManager = GameStateManager.getInstance();
    this.floorData = data.floorData || this.gameStateManager.getCurrentFloorData();

    // Reset state flags when scene is reinitialized
    this.isProcessingMove = false;
    this.isPlayerTurn = true;
    this.selectedSquare = null;
    this.validMoves = [];
    this.abilityButtons = [];
    this.teleporterGraphics = [];
    this.stunIndicators = new Map();
    this.chessClock = undefined;
    this.obstacleMode = 'none';
    this.selectedWallSquare = null;
    this.selectedWallDirection = null;
    this.obstacleGraphicsObjects = [];
    this.cancelTargetingButton = undefined;
    this.quietHalfmoves = 0;
    this.positionCounts = new Map();
    this.lastMove = null;
    this.markerLabels = [];
    this.moveFx = [];
    this.messageText = undefined;
    this.messageTween = undefined;
  }

  private debugText?: Phaser.GameObjects.Text;

  create(): void {
    console.log('Battle Scene Started - Floor', this.floorData.floorNumber);
    AudioManager.getInstance().init(this);
    paintBackground(this, GAME_CONFIG.WIDTH, GAME_CONFIG.HEIGHT);
    fadeInScene(this);
    this.ensureSparkTexture();

    // Look up encounter if this is an elite/boss fight
    this.encounter = this.floorData.encounterId
      ? ENCOUNTER_DATABASE[this.floorData.encounterId] ?? null
      : null;

    // Initialize chess game with AI based on floor difficulty (debug override > encounter > floor default)
    const floor = this.floorData.floorNumber;
    const debugOverride = this.gameStateManager.getAIConfigOverride();
    const aiConfig = debugOverride ?? this.encounter?.aiConfig ?? AI_DIFFICULTY[Math.min(floor, 12)] ?? AI_DIFFICULTY[1];
    if (debugOverride) {
      console.log('BattleScene: using debug AI override', debugOverride);
    }

    // Resolve obstacles early so pawn placement can avoid blocked columns
    const obstacles = this.encounter?.obstacles ?? this.floorData.obstacles;
    if (obstacles && obstacles.length > 0) {
      this.floorData.obstacles = obstacles;
    }

    // Generate starting FEN based on floor (minimal setup for floor 1)
    const startingFen = this.getStartingFen(floor);

    // Progressive board size expansion based on floor
    const restrictedArea = this.getBoardRestrictions(floor);

    this.chessGame = new ChessGame(startingFen, aiConfig, restrictedArea);

    // Apply obstacles to chess engine
    if (this.floorData.obstacles && this.floorData.obstacles.length > 0) {
      this.chessGame.setObstacles(this.floorData.obstacles);
    }

    // Pass active upgrades to chess game for mechanical effects
    const activeUpgrades = this.gameStateManager.getActiveUpgrades();
    this.chessGame.setActiveUpgrades(activeUpgrades);

    // Debug text at bottom of screen (dev builds only)
    if (import.meta.env.DEV) {
      this.debugText = this.add.text(10, 1200, '', {
        fontSize: '14px',
        color: '#00ff00',
        backgroundColor: '#000000',
        padding: { x: 5, y: 5 }
      });
      this.debugText.setDepth(10000);
      this.updateDebugText(`Floor ${floor} loaded. Click a piece to see debug info.`);
    }


    // Initialize piece health
    this.initializePieceHealth();

    // Create board
    this.createBoard();

    // Create pieces
    this.createPieces();

    // Create UI
    this.createUI();

    // Draw piece roster
    this.drawPieceRoster();

    // Setup input
    this.setupInput();

    // Reset battle state
    this.chessGame.resetBattleState();
    this.playerTurnNumber = 0;
    this.isExtraMoveActive = false;
    this.extraMovePiece = null;

    // === Battle-start upgrade effects ===
    this.applyBattleStartUpgrades();

    // === Chess clock ===
    this.setupChessClock(floor);

    // === Fog of war ===
    this.setupFogOfWar();

    // === Teleporter rendering ===
    this.renderTeleporters();

    // === Ability buttons ===
    this.createAbilityButtons();

    // Initial board markers (sacred ground / bounty placed at battle start)
    this.updateBoardMarkers();

    // Start player turn
    this.isPlayerTurn = true;
    this.updateTurnDisplay();

    // Show AI move preview if upgrade active
    this.showAIMovePreviewIfNeeded();

    // Update debug with turn state
    this.updateDebugText(`Floor ${floor} | Turn: ${this.isPlayerTurn ? 'PLAYER' : 'AI'} | Processing: ${this.isProcessingMove}`);

    // First-ever battle: gentle one-time tutorial
    this.maybeShowTutorial();
  }

  /**
   * One-time tutorial overlay shown on the player's first battle. Explains the
   * tap-to-move flow and the win/lose conditions, then never appears again.
   */
  private maybeShowTutorial(): void {
    let seen = false;
    try { seen = localStorage.getItem('chessrogue_tutorial_seen') === '1'; } catch { seen = false; }
    if (seen) return;

    const { WIDTH, HEIGHT } = GAME_CONFIG;
    const cx = WIDTH / 2;
    const depth = Z_DEPTHS.MODAL + 50;
    const els: Phaser.GameObjects.GameObject[] = [];

    const overlay = this.add.rectangle(0, 0, WIDTH, HEIGHT, 0x0a0f1c, 0.82).setOrigin(0);
    overlay.setDepth(depth);
    overlay.setInteractive();
    els.push(overlay);

    const panelH = 520;
    const panel = makePanel(this, cx, HEIGHT / 2, 600, panelH, { fill: PALETTE.panel, stroke: PALETTE.gold, strokeWidth: 2, radius: 24 });
    panel.setDepth(depth + 1);
    els.push(panel);

    const top = HEIGHT / 2 - panelH / 2;
    const title = this.add.text(cx, top + 44, 'HOW TO FIGHT', T.heading({ color: INK.gold }));
    title.setOrigin(0.5); title.setLetterSpacing(2); title.setDepth(depth + 2);
    els.push(title);

    const tips: Array<[string, string]> = [
      ['♟', 'Tap one of your pieces, then tap a highlighted square to move it.'],
      ['◎', 'Green dots are quiet moves. Red rings are captures.'],
      ['♚', 'Win by capturing the enemy down to their lone king — or checkmate.'],
      ['✦', 'Lose all your fighting pieces and the run ends. Choose moves wisely!'],
    ];
    tips.forEach(([icon, text], i) => {
      const y = top + 110 + i * 84;
      const ic = this.add.text(cx - 250, y, icon, { fontFamily: T.body().fontFamily, fontSize: '30px', color: INK.gold });
      ic.setOrigin(0.5, 0); ic.setDepth(depth + 2); els.push(ic);
      const tx = this.add.text(cx - 210, y, text, T.body({ color: INK.secondary, wordWrap: { width: 430 }, lineSpacing: 3 }));
      tx.setOrigin(0, 0); tx.setDepth(depth + 2); els.push(tx);
    });

    const btn = makeButton(this, cx, top + panelH - 56, "LET'S GO", {
      width: 300, fill: PALETTE.green, depth: depth + 2,
      onClick: () => {
        try { localStorage.setItem('chessrogue_tutorial_seen', '1'); } catch { /* ignore */ }
        for (const e of els) e.destroy();
        btn.container.destroy();
      },
    });
  }

  /**
   * Get board size restrictions based on floor number
   * Floor 1-7: 6x6 (b-g, ranks 2-7)
   * Floor 8-11: 7x7 (b-h, ranks 2-8)
   * Floor 12+: 8x8 (full board)
   */
  private getBoardRestrictions(floor: number): { minCol: number; maxCol: number; minRank: number; maxRank: number } | undefined {
    if (floor <= 7) {
      // 6x6: columns b-g (1-6), ranks 2-7
      return { minCol: 1, maxCol: 6, minRank: 2, maxRank: 7 };
    } else if (floor <= 11) {
      // 7x7: columns b-h (1-7), ranks 2-8
      return { minCol: 1, maxCol: 7, minRank: 2, maxRank: 8 };
    } else {
      // 8x8: full board
      return undefined;
    }
  }

  private getStartingFen(floor: number): string | undefined {
    // Get player's saved pieces (for types and upgrades, not positions)
    const playerPieces = this.gameStateManager.getPlayerPiecesWithRelics();

    // Get enemy pieces: encounter overrides default floor-based pieces
    const enemyPieces = this.repositionEnemyPawns(
      this.encounter?.enemyPieces ?? this.getDefaultEnemyPieces(floor),
      floor
    );

    // Try up to 50 different random starting positions
    const maxAttempts = 50;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      // Reassign pieces to proper starting positions based on floor
      // Add attempt number to seed to vary positions
      const repositionedPieces = this.assignStartingPositions(playerPieces, floor, attempt);

      // Generate FEN
      const fen = this.generateFenFromPieces(repositionedPieces, { enemyPieces });

      // Validate the position
      if (this.isValidStartingPosition(fen)) {
        console.log(`Valid starting position found on attempt ${attempt + 1}`);
        // Store the repositioned pieces for later use in initializePieceHealth
        this.repositionedPlayerPieces = repositionedPieces;
        return fen;
      } else {
        console.log(`Invalid starting position on attempt ${attempt + 1}, trying again...`);
      }
    }

    // Fallback: if we couldn't find a valid position after max attempts,
    // use a simple non-randomized layout
    console.warn('Could not find valid random position after', maxAttempts, 'attempts. Using fallback position.');
    const fallbackPieces = this.assignStartingPositions(playerPieces, floor, -1); // -1 = no randomization
    this.repositionedPlayerPieces = fallbackPieces;
    return this.generateFenFromPieces(fallbackPieces, { enemyPieces });
  }

  /**
   * Get default enemy piece composition based on floor tier
   */
  private getDefaultEnemyPieces(floor: number): Array<{ type: PieceType; square: Square }> {
    const seed = this.gameStateManager.getGameState().currentRun?.seed || 'default';
    return generateEnemyArmy(floor, seed);
  }

  /**
   * Assign starting positions to player pieces based on floor layout
   */
  private assignStartingPositions(pieces: PieceData[], floor: number, attempt: number = 0): PieceData[] {
    console.log('=== ASSIGN STARTING POSITIONS ===');
    console.log('Floor:', floor, 'Attempt:', attempt);
    console.log('Input pieces:', pieces.map(p => `${p.type}@${p.square} color:${p.color}`).join(', '));

    // Determine starting ranks and files based on floor
    let startRank: number;
    let files: string[];

    if (floor <= 7) {
      // 6x6: b-g, ranks 2-7
      startRank = 2;
      files = ['b', 'c', 'd', 'e', 'f', 'g'];
    } else if (floor <= 11) {
      // 7x7: b-h, ranks 2-8
      startRank = 2;
      files = ['b', 'c', 'd', 'e', 'f', 'g', 'h'];
    } else {
      // 8x8: full board
      startRank = 1;
      files = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];
    }

    const secondRank = startRank + 1;

    console.log('Start rank:', startRank, 'Second rank:', secondRank);
    console.log('Available files:', files);

    // Get run seed for deterministic randomization
    const gameState = this.gameStateManager.getGameState();
    const seed = gameState.currentRun?.seed || Math.random().toString();

    // Create seeded random function
    // If attempt is -1, don't randomize (fallback mode)
    // Otherwise, add attempt number to vary the seed
    const seededRandom = attempt === -1
      ? (() => 0.5) // Deterministic non-random for fallback
      : this.createSeededRandom(seed + floor + attempt);

    // Shuffle files for random placement
    const shuffledBackRankFiles = this.shuffleArray([...files], seededRandom);
    let shuffledPawnFiles = this.shuffleArray([...files], seededRandom);

    // Prioritize clear columns for white pawns (avoid water/wall obstacles)
    const obstacles = this.floorData?.obstacles;
    const maxRank = floor <= 7 ? 7 : floor <= 11 ? 8 : 8;
    const blockedFiles = this.getObstacleBlockedFiles(obstacles, secondRank, maxRank);
    // No existing pawns to track for white (we're placing them all now)
    shuffledPawnFiles = this.prioritizeClearFiles(shuffledPawnFiles, blockedFiles, new Set());

    // Separate pieces by type for intelligent placement
    const kings = pieces.filter(p => p.type === 'k');
    const queens = pieces.filter(p => p.type === 'q');
    const rooks = pieces.filter(p => p.type === 'r');
    const bishops = pieces.filter(p => p.type === 'b');
    const knights = pieces.filter(p => p.type === 'n');
    const pawns = pieces.filter(p => p.type === 'p');

    console.log('Piece counts - Kings:', kings.length, 'Queens:', queens.length, 'Rooks:', rooks.length,
                'Bishops:', bishops.length, 'Knights:', knights.length, 'Pawns:', pawns.length);

    const positioned: PieceData[] = [];
    let backRankFileIndex = 0;
    let pawnFileIndex = 0;

    // Helper to assign piece to next available square
    const assignSquare = (piece: PieceData, preferBackRank: boolean = true): void => {
      const rank = preferBackRank ? startRank : secondRank;
      let file: string;

      if (preferBackRank) {
        file = shuffledBackRankFiles[backRankFileIndex % shuffledBackRankFiles.length];
        backRankFileIndex++;
      } else {
        file = shuffledPawnFiles[pawnFileIndex % shuffledPawnFiles.length];
        pawnFileIndex++;
      }

      positioned.push({
        ...piece,
        square: `${file}${rank}` as any,
      });
    };

    // Place pieces in logical order: King first, then major pieces, then pawns
    kings.forEach(p => assignSquare(p));
    queens.forEach(p => assignSquare(p));
    rooks.forEach(p => assignSquare(p));
    bishops.forEach(p => assignSquare(p));
    knights.forEach(p => assignSquare(p));

    // Pawns on second rank with separate shuffled files
    pawns.forEach(p => assignSquare(p, false));

    console.log('Positioned pieces:', positioned.map(p => `${p.type}@${p.square} color:${p.color}`).join(', '));
    return positioned;
  }

  /**
   * Validate that a starting position doesn't have either player in check or stalemate
   */
  private isValidStartingPosition(fen: string): boolean {
    try {
      // Create a temporary chess game to validate the position
      const restrictedArea = this.getBoardRestrictions(this.floorData.floorNumber);
      const tempGame = new ChessGame(fen, AI_DIFFICULTY[1], restrictedArea);

      // Check if white is in check (white moves first)
      if (tempGame.isCheck()) {
        console.log('Position invalid: White starts in check');
        tempGame.destroy();
        return false;
      }

      // Check if white is in stalemate (no legal moves)
      if (tempGame.isStalemate()) {
        console.log('Position invalid: White has no legal moves (stalemate)');
        tempGame.destroy();
        return false;
      }

      // Validate black's position by creating a separate game with black to move
      const blackFen = fen.replace(' w ', ' b ');
      const tempGame2 = new ChessGame(blackFen, AI_DIFFICULTY[1], restrictedArea);

      if (tempGame2.isCheck()) {
        console.log('Position invalid: Black starts in check');
        tempGame2.destroy();
        tempGame.destroy();
        return false;
      }

      if (tempGame2.isStalemate()) {
        console.log('Position invalid: Black has no legal moves (stalemate)');
        tempGame2.destroy();
        tempGame.destroy();
        return false;
      }

      // Position is valid
      tempGame2.destroy();
      tempGame.destroy();
      return true;
    } catch (error) {
      console.error('Error validating starting position:', error);
      return false; // If validation fails, try another position
    }
  }

  /**
   * Create a seeded random function
   */
  private createSeededRandom(seed: string): () => number {
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
      const char = seed.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash | 0;
    }

    return () => {
      hash = (hash * 9301 + 49297) % 233280;
      if (hash < 0) {
        hash += 233280;
      }
      return hash / 233280;
    };
  }

  /**
   * Reposition enemy (black) pawns to prefer columns without water/wall obstacles.
   * Non-pawn pieces keep their original squares. Pawns swap among themselves
   * to prioritize clear columns.
   */
  private repositionEnemyPawns(
    enemyPieces: Array<{ type: PieceType; square: Square }>,
    floor: number
  ): Array<{ type: PieceType; square: Square }> {
    const obstacles = this.floorData?.obstacles;
    if (!obstacles || obstacles.length === 0) return enemyPieces;

    const nonPawns = enemyPieces.filter(p => p.type !== 'p');
    const pawns = enemyPieces.filter(p => p.type === 'p');
    if (pawns.length === 0) return enemyPieces;

    // Black pawns need to advance downward (decreasing rank numbers)
    const minRank = floor <= 7 ? 2 : floor <= 11 ? 2 : 1;
    const pawnRank = parseInt(pawns[0].square[1]);
    const blockedFiles = this.getObstacleBlockedFiles(obstacles, minRank, pawnRank);

    // Track files occupied by non-pawn enemy pieces (can't place pawns there)
    const nonPawnFiles = new Set(nonPawns.map(p => {
      const rank = parseInt(p.square[1]);
      return rank === pawnRank ? p.square[0] : null;
    }).filter(Boolean) as string[]);

    // Get all available files for this board size
    let allFiles: string[];
    if (floor <= 7) {
      allFiles = ['b', 'c', 'd', 'e', 'f', 'g'];
    } else if (floor <= 11) {
      allFiles = ['b', 'c', 'd', 'e', 'f', 'g', 'h'];
    } else {
      allFiles = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];
    }

    // Build prioritized file list: clear files first, then blocked files
    // Only consider files not occupied by non-pawn pieces on the pawn rank
    const availableFiles = allFiles.filter(f => !nonPawnFiles.has(f));
    const clearFiles = availableFiles.filter(f => !blockedFiles.has(f));
    const blockedAvailable = availableFiles.filter(f => blockedFiles.has(f));
    const prioritized = [...clearFiles, ...blockedAvailable];

    // Assign pawns to prioritized files
    const repositionedPawns = pawns.map((p, i) => ({
      type: p.type,
      square: `${prioritized[i % prioritized.length]}${pawnRank}` as Square,
    }));

    return [...nonPawns, ...repositionedPawns];
  }

  /**
   * Get files (columns) that have water or wall obstacles blocking pawn advancement.
   * Returns a Set of file letters that contain obstacles between the given ranks.
   */
  private getObstacleBlockedFiles(obstacles: Obstacle[] | undefined, minRank: number, maxRank: number): Set<string> {
    const blocked = new Set<string>();
    if (!obstacles) return blocked;
    for (const obs of obstacles) {
      if (obs.type === 'water' || obs.type === 'wall') {
        const file = obs.square[0];
        const rank = parseInt(obs.square[1]);
        if (rank >= minRank && rank <= maxRank) {
          blocked.add(file);
        }
      }
    }
    return blocked;
  }

  /**
   * Reorder files so that unblocked columns (no obstacle AND no existing pawn) come first,
   * preserving the shuffled order within each group.
   */
  private prioritizeClearFiles(
    shuffledFiles: string[],
    blockedFiles: Set<string>,
    occupiedPawnFiles: Set<string>
  ): string[] {
    const clear: string[] = [];
    const obstructed: string[] = [];
    for (const f of shuffledFiles) {
      if (!blockedFiles.has(f) && !occupiedPawnFiles.has(f)) {
        clear.push(f);
      } else {
        obstructed.push(f);
      }
    }
    return [...clear, ...obstructed];
  }

  /**
   * Shuffle array using Fisher-Yates algorithm
   */
  private shuffleArray<T>(array: T[], random: () => number): T[] {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }

  /**
   * Generate FEN string from player pieces and enemy pieces
   */
  private generateFenFromPieces(
    playerPieces: PieceData[],
    options: { enemyPieces: Array<{ type: PieceType; square: Square }> }
  ): string {
    // Create board array (8x8)
    const board: (string | null)[][] = Array(8).fill(null).map(() => Array(8).fill(null));

    // Place player pieces (white)
    for (const piece of playerPieces) {
      const file = piece.square.charCodeAt(0) - 97; // 'a' = 0
      const rank = parseInt(piece.square[1]) - 1; // '1' = 0
      const row = 7 - rank; // Convert to array index (rank 8 = row 0)

      board[row][file] = piece.type.toUpperCase(); // White pieces are uppercase
    }

    // Place enemy pieces (black)
    for (const piece of options.enemyPieces) {
      const file = piece.square.charCodeAt(0) - 97;
      const rank = parseInt(piece.square[1]) - 1;
      const row = 7 - rank;

      board[row][file] = piece.type.toLowerCase(); // Black pieces are lowercase
    }

    // Convert board to FEN
    const fenRanks: string[] = [];
    for (let row = 0; row < 8; row++) {
      let fenRank = '';
      let emptyCount = 0;

      for (let col = 0; col < 8; col++) {
        const piece = board[row][col];
        if (piece === null) {
          emptyCount++;
        } else {
          if (emptyCount > 0) {
            fenRank += emptyCount.toString();
            emptyCount = 0;
          }
          fenRank += piece;
        }
      }

      if (emptyCount > 0) {
        fenRank += emptyCount.toString();
      }

      fenRanks.push(fenRank);
    }

    const fen = `${fenRanks.join('/')} w - - 0 1`;
    console.log('=== FEN GENERATION ===');
    console.log('Generated FEN:', fen);
    console.log('Player pieces:', playerPieces.map(p => `${p.type}@${p.square} (color: ${p.color})`).join(', '));
    console.log('Enemy pieces:', options.enemyPieces.map(p => `${p.type}@${p.square}`).join(', '));
    return fen;
  }

  private initializePieceHealth(): void {
    const board = this.chessGame.getBoard();

    // Use the repositioned player pieces (with correct new squares) instead of saved pieces
    const playerPieceMap = new Map(this.repositionedPlayerPieces.map(p => [p.square, p]));

    // Clear previous battle state
    this.pieces.clear();
    this.originalPieceTypes.clear();

    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        const piece = board[row][col];
        if (piece) {
          const square = this.indexToSquare(col, row);

          // For white (player) pieces, use repositioned data with upgrades
          if (piece.color === 'w' && playerPieceMap.has(square)) {
            const repositionedPiece = playerPieceMap.get(square)!;
            this.pieces.set(square, {
              ...repositionedPiece,
              square, // Ensure square is updated
            });
            // Track original type for pawn promotion reversion
            this.originalPieceTypes.set(square, repositionedPiece.type);
          } else {
            // For black (AI) pieces or pieces without saved data, create new
            this.pieces.set(square, {
              type: piece.type,
              color: piece.color,
              square,
              upgrades: [],
            });
            // Track original type
            this.originalPieceTypes.set(square, piece.type);
          }
        }
      }
    }
  }

  /**
   * Get board display configuration based on floor
   */
  private getBoardDisplayConfig(): { boardSize: number; startCol: number; startRow: number } {
    const floor = this.floorData.floorNumber;

    if (floor <= 7) {
      // 6x6: b-g (cols 1-6), ranks 2-7 (rows 1-6)
      return { boardSize: 6, startCol: 1, startRow: 1 };
    } else if (floor <= 11) {
      // 7x7: b-h (cols 1-7), ranks 2-8 (rows 0-6)
      return { boardSize: 7, startCol: 1, startRow: 0 };
    } else {
      // 8x8: full board
      return { boardSize: 8, startCol: 0, startRow: 0 };
    }
  }

  /**
   * Vertical origin of the board, centered in the play area between the header
   * and the bottom controls. Larger boards start higher so they always fit.
   */
  private getBoardStartY(): number {
    const { boardSize } = this.getBoardDisplayConfig();
    const boardPx = boardSize * BOARD_CONFIG.CELL_SIZE;
    const headerBottom = 185;
    const controlsTop = GAME_CONFIG.HEIGHT - 210;
    return Math.round(headerBottom + Math.max(0, (controlsTop - headerBottom - boardPx) / 2));
  }

  private createBoard(): void {
    this.boardGraphics = this.add.graphics();
    this.boardGraphics.setDepth(Z_DEPTHS.BOARD);

    const { CELL_SIZE, COLORS } = BOARD_CONFIG;

    const { boardSize, startCol, startRow } = this.getBoardDisplayConfig();

    const startX = (GAME_CONFIG.WIDTH - CELL_SIZE * boardSize) / 2;
    const startY = this.getBoardStartY();

    // Draw checkerboard
    for (let row = 0; row < boardSize; row++) {
      for (let col = 0; col < boardSize; col++) {
        // Actual board position
        const actualCol = startCol + col;
        const actualRow = startRow + row;

        const isLight = (actualRow + actualCol) % 2 === 0;
        const color = isLight ? COLORS.LIGHT : COLORS.DARK;

        const x = startX + col * CELL_SIZE;
        const y = startY + row * CELL_SIZE;

        this.boardGraphics.fillStyle(color, 1);
        this.boardGraphics.fillRect(x, y, CELL_SIZE, CELL_SIZE);

        // Draw coordinates (for debugging)
        const square = this.indexToSquare(actualCol, actualRow);
        const coordText = this.add.text(x + 4, y + 4, square, {
          fontSize: '10px',
          color: isLight ? '#000' : '#fff',
        });
        coordText.setAlpha(0.3);
      }
    }

    // Persistent marker layer (last move, check, sacred ground, bounty) under
    // the move highlights
    this.markerGraphics = this.add.graphics();
    this.markerGraphics.setDepth(Z_DEPTHS.OBSTACLES + 1);

    // Create highlight graphics layer
    this.highlightGraphics = this.add.graphics();
    this.highlightGraphics.setDepth(Z_DEPTHS.HIGHLIGHTS);

    // Draw obstacles if any
    if (this.floorData.obstacles && this.floorData.obstacles.length > 0) {
      this.drawObstacles(boardSize, startCol, startRow);
    }
  }

  private drawObstacles(boardSize: number, startCol: number, startRow: number): void {
    if (!this.floorData.obstacles) return;

    const { CELL_SIZE } = BOARD_CONFIG;
    const startX = (GAME_CONFIG.WIDTH - CELL_SIZE * boardSize) / 2;
    const startY = this.getBoardStartY();

    for (const obstacle of this.floorData.obstacles) {
      const col = obstacle.square.charCodeAt(0) - 97; // 'a' = 0
      const row = 8 - parseInt(obstacle.square[1]); // '8' = 0

      // Calculate position relative to restricted board
      const relativeCol = col - startCol;
      const relativeRow = row - startRow;

      // Skip if outside visible board
      if (relativeCol < 0 || relativeCol >= boardSize || relativeRow < 0 || relativeRow >= boardSize) {
        continue;
      }

      const x = startX + relativeCol * CELL_SIZE;
      const y = startY + relativeRow * CELL_SIZE;

      // Draw obstacle based on type
      switch (obstacle.type) {
        case 'wall':
          this.drawWall(x, y, CELL_SIZE, obstacle.blocksDirection);
          break;
        case 'water':
          this.drawWater(x, y, CELL_SIZE);
          break;
        case 'void':
          this.drawVoid(x, y, CELL_SIZE);
          break;
        case 'pit':
          this.drawPit(x, y, CELL_SIZE);
          break;
      }
    }
  }

  private drawWall(x: number, y: number, size: number, direction?: string): void {
    const wallGraphics = this.add.graphics();
    const wallThickness = 8; // Thickness of the wall

    // Draw wall on the appropriate edge based on direction
    switch (direction) {
      case 'north':
        // Top edge of the square
        wallGraphics.fillStyle(0x555555, 1);
        wallGraphics.fillRect(x, y - wallThickness / 2, size, wallThickness);

        // Brick pattern
        wallGraphics.fillStyle(0x444444, 1);
        for (let i = 0; i < size; i += size / 4) {
          wallGraphics.fillRect(x + i, y - wallThickness / 2, 2, wallThickness);
        }
        break;

      case 'south':
        // Bottom edge of the square
        wallGraphics.fillStyle(0x555555, 1);
        wallGraphics.fillRect(x, y + size - wallThickness / 2, size, wallThickness);

        // Brick pattern
        wallGraphics.fillStyle(0x444444, 1);
        for (let i = 0; i < size; i += size / 4) {
          wallGraphics.fillRect(x + i, y + size - wallThickness / 2, 2, wallThickness);
        }
        break;

      case 'east':
        // Right edge of the square
        wallGraphics.fillStyle(0x555555, 1);
        wallGraphics.fillRect(x + size - wallThickness / 2, y, wallThickness, size);

        // Brick pattern
        wallGraphics.fillStyle(0x444444, 1);
        for (let i = 0; i < size; i += size / 4) {
          wallGraphics.fillRect(x + size - wallThickness / 2, y + i, wallThickness, 2);
        }
        break;

      case 'west':
        // Left edge of the square
        wallGraphics.fillStyle(0x555555, 1);
        wallGraphics.fillRect(x - wallThickness / 2, y, wallThickness, size);

        // Brick pattern
        wallGraphics.fillStyle(0x444444, 1);
        for (let i = 0; i < size; i += size / 4) {
          wallGraphics.fillRect(x - wallThickness / 2, y + i, wallThickness, 2);
        }
        break;

      default:
        // Old-style full-square wall
        wallGraphics.fillStyle(0x666666, 1);
        wallGraphics.fillRect(x, y, size, size);

        // Brick pattern
        wallGraphics.lineStyle(2, 0x444444, 1);
        for (let i = 0; i < size; i += size / 3) {
          wallGraphics.lineBetween(x, y + i, x + size, y + i);
        }
        for (let i = 0; i < size; i += size / 2) {
          wallGraphics.lineBetween(x + i, y, x + i, y + size);
        }

        const wallText = this.add.text(x + size / 2, y + size / 2, '█', {
          fontSize: `${size * 0.8}px`,
          color: '#888888',
        });
        wallText.setOrigin(0.5);
        wallText.setDepth(Z_DEPTHS.BOARD + 2);
        this.obstacleGraphicsObjects.push(wallText);
        break;
    }

    wallGraphics.setDepth(Z_DEPTHS.BOARD + 1);
    this.obstacleGraphicsObjects.push(wallGraphics);
  }

  private drawWater(x: number, y: number, size: number): void {
    // Blue water filling entire square
    const waterGraphics = this.add.graphics();
    waterGraphics.fillStyle(0x3498db, 0.8);
    waterGraphics.fillRect(x, y, size, size);

    // Border for definition
    waterGraphics.lineStyle(2, 0x2980b9, 0.6);
    waterGraphics.strokeRect(x, y, size, size);

    waterGraphics.setDepth(Z_DEPTHS.BOARD + 1);
    this.obstacleGraphicsObjects.push(waterGraphics);

    // Water icon
    const waterText = this.add.text(x + size / 2, y + size / 2, '≈', {
      fontSize: `${size * 0.6}px`,
      color: '#2980b9',
    });
    waterText.setOrigin(0.5);
    waterText.setDepth(Z_DEPTHS.BOARD + 2);
    this.obstacleGraphicsObjects.push(waterText);
  }

  private drawVoid(x: number, y: number, size: number): void {
    // Black void with purple border
    const voidGraphics = this.add.graphics();
    voidGraphics.fillStyle(0x000000, 0.9);
    voidGraphics.fillRect(x, y, size, size);

    voidGraphics.lineStyle(3, 0x9b59b6, 0.8);
    voidGraphics.strokeRect(x, y, size, size);

    voidGraphics.setDepth(Z_DEPTHS.BOARD + 1);
    this.obstacleGraphicsObjects.push(voidGraphics);

    // Void icon
    const voidText = this.add.text(x + size / 2, y + size / 2, 'X', {
      fontSize: `${size * 0.6}px`,
      color: '#9b59b6',
      fontStyle: 'bold',
    });
    voidText.setOrigin(0.5);
    voidText.setDepth(Z_DEPTHS.BOARD + 2);
    this.obstacleGraphicsObjects.push(voidText);
  }

  private drawPit(x: number, y: number, size: number): void {
    // Blue water-like pit filling entire square
    const pitGraphics = this.add.graphics();
    pitGraphics.fillStyle(0x3498db, 0.6);
    pitGraphics.fillRect(x, y, size, size);

    // Wave pattern
    pitGraphics.lineStyle(2, 0x5dade2, 0.8);
    pitGraphics.beginPath();
    pitGraphics.moveTo(x, y + size / 2);
    for (let i = 0; i < size; i += size / 4) {
      pitGraphics.lineTo(x + i + size / 8, y + size / 2 - size / 8);
      pitGraphics.lineTo(x + i + size / 4, y + size / 2);
    }
    pitGraphics.strokePath();

    pitGraphics.setDepth(Z_DEPTHS.BOARD + 1);
    this.obstacleGraphicsObjects.push(pitGraphics);

    // Pit icon (wave symbol)
    const pitText = this.add.text(x + size / 2, y + size / 2, '≈', {
      fontSize: `${size * 0.6}px`,
      color: '#2980b9',
    });
    pitText.setOrigin(0.5);
    pitText.setDepth(Z_DEPTHS.BOARD + 2);
    this.obstacleGraphicsObjects.push(pitText);
  }

  private createPieces(): void {
    const board = this.chessGame.getBoard();
    const { CELL_SIZE } = BOARD_CONFIG;

    const { boardSize, startCol, startRow } = this.getBoardDisplayConfig();

    const startX = (GAME_CONFIG.WIDTH - CELL_SIZE * boardSize) / 2;
    const startY = this.getBoardStartY();

    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        const piece = board[row][col];
        if (piece) {
          // Skip pieces outside the restricted board area
          if (col < startCol || col >= startCol + boardSize ||
              row < startRow || row >= startRow + boardSize) {
            continue;
          }

          const square = this.indexToSquare(col, row);
          const spriteKey = `${piece.color}${piece.type}`;

          // Calculate position relative to restricted board
          const relativeCol = col - startCol;
          const relativeRow = row - startRow;

          const x = startX + relativeCol * CELL_SIZE + CELL_SIZE / 2;
          const y = startY + relativeRow * CELL_SIZE + CELL_SIZE / 2;

          const sprite = this.add.sprite(x, y, spriteKey);
          sprite.setDisplaySize(CELL_SIZE * 0.8, CELL_SIZE * 0.8);
          sprite.setDepth(Z_DEPTHS.PIECES);
          sprite.setData('square', square);
          sprite.setInteractive({ useHandCursor: true });

          this.pieceSprites.set(square, sprite);

          // Staggered drop-in at battle start for a satisfying cascade
          const baseScale = sprite.scaleX;
          sprite.setScale(0);
          this.tweens.add({
            targets: sprite,
            scaleX: baseScale,
            scaleY: baseScale,
            ease: 'Back.out',
            duration: 280,
            delay: 120 + (row + col) * 22,
          });
        }
      }
    }
  }

  /**
   * A small rounded HUD pill with auto-sized background. originX 0 = left-aligned
   * at x, 1 = right-aligned at x.
   */
  private hudChip(x: number, y: number, text: string, opts: { originX: number; color: string }): Phaser.GameObjects.Text {
    const label = this.add.text(0, 0, text, T.label({ color: opts.color }));
    const padX = 14, padY = 8;
    const w = label.width + padX * 2;
    const h = label.height + padY * 2;
    const bgX = opts.originX === 1 ? x - w : x;
    const g = this.add.graphics();
    g.fillStyle(PALETTE.panel, 0.92);
    g.fillRoundedRect(bgX, y, w, h, 12);
    g.lineStyle(1, PALETTE.stroke, 0.8);
    g.strokeRoundedRect(bgX, y, w, h, 12);
    g.setDepth(Z_DEPTHS.UI);
    label.setPosition(bgX + padX, y + padY);
    label.setDepth(Z_DEPTHS.UI + 1);
    return label;
  }

  private createUI(): void {
    // Floor indicator chip
    this.hudChip(20, 18, `FLOOR ${this.floorData.floorNumber}  ·  ${this.floorData.difficulty.toUpperCase()}`, {
      originX: 0, color: INK.primary,
    });

    // Encounter banner (for elite/boss fights)
    if (this.encounter) {
      const colorStr = '#' + this.encounter.accentColor.toString(16).padStart(6, '0');
      const nameBanner = this.add.text(GAME_CONFIG.WIDTH / 2, 138, this.encounter.name,
        T.subheading({ color: colorStr, fontSize: '28px', fontStyle: '700' }));
      nameBanner.setOrigin(0.5);
      nameBanner.setDepth(Z_DEPTHS.UI);

      if (this.encounter.battleTagline) {
        const tagline = this.add.text(GAME_CONFIG.WIDTH / 2, 168, this.encounter.battleTagline,
          T.small({ color: INK.faint, fontStyle: 'italic' }));
        tagline.setOrigin(0.5);
        tagline.setDepth(Z_DEPTHS.UI);
      }
    }

    // Active relics display
    this.displayActiveRelics();

    // Gold chip
    const gameState = this.gameStateManager.getGameState();
    this.hudChip(GAME_CONFIG.WIDTH - 20, 18, `◆ ${gameState.gold}`, { originX: 1, color: INK.gold });

    // Turn indicator
    this.turnText = this.add.text(GAME_CONFIG.WIDTH / 2, 100, '', T.heading());
    this.turnText.setOrigin(0.5);
    this.turnText.setLetterSpacing(2);
    this.turnText.setDepth(Z_DEPTHS.UI);

    // Instructions
    const instructions = this.add.text(GAME_CONFIG.WIDTH / 2, GAME_CONFIG.HEIGHT - 46,
      'Tap a piece, then tap a highlighted square to move', T.small({ color: INK.faint, align: 'center' }));
    instructions.setOrigin(0.5);
    instructions.setDepth(Z_DEPTHS.UI);

    // DEV: Skip button for testing (dev builds only)
    if (!import.meta.env.DEV) return;
    const skipButton = this.add.text(
      GAME_CONFIG.WIDTH / 2,
      GAME_CONFIG.HEIGHT - 20,
      '[DEV] SKIP LEVEL',
      {
        fontSize: '14px',
        color: '#ffffff',
        backgroundColor: '#e74c3c',
        padding: { x: 10, y: 5 },
        fontStyle: 'bold',
      }
    );
    skipButton.setOrigin(0.5);
    skipButton.setDepth(Z_DEPTHS.UI);
    skipButton.setInteractive({ useHandCursor: true });

    skipButton.on('pointerover', () => {
      skipButton.setScale(1.1);
      skipButton.setBackgroundColor('#c0392b');
    });

    skipButton.on('pointerout', () => {
      skipButton.setScale(1.0);
      skipButton.setBackgroundColor('#e74c3c');
    });

    skipButton.on('pointerdown', () => {
      // Instantly win the level
      this.gameOver(true);
    });
  }

  private drawPieceRoster(): void {
    const gameState = this.gameStateManager.getGameState();
    const currentPieces = gameState.playerPieces;
    const capturedPieces = gameState.capturedPieces;

    const pieceSize = 40;
    const spacing = 50;

    // Collect all pieces (individually, including each pawn)
    const allPieces: { type: PieceType; isAlive: boolean }[] = [];

    // Standard chess order for display
    const pieceOrder: PieceType[] = ['k', 'q', 'r', 'b', 'n', 'p'];

    for (const pieceType of pieceOrder) {
      const alivePieces = currentPieces.filter(p => p.type === pieceType);
      const capturedCount = capturedPieces.filter(t => t === pieceType).length;

      // Add alive pieces
      for (let i = 0; i < alivePieces.length; i++) {
        allPieces.push({ type: pieceType, isAlive: true });
      }

      // Add captured pieces
      for (let i = 0; i < capturedCount; i++) {
        allPieces.push({ type: pieceType, isAlive: false });
      }
    }

    // Calculate total width and center position
    const totalWidth = allPieces.length * spacing;
    const startX = (GAME_CONFIG.WIDTH - totalWidth) / 2 + spacing / 2;
    const y = GAME_CONFIG.HEIGHT - 100;

    // Draw each piece
    allPieces.forEach((piece, index) => {
      const x = startX + index * spacing;

      if (piece.isAlive) {
        // Draw solid piece sprite
        const spriteKey = `w${piece.type}`;
        if (this.textures.exists(spriteKey)) {
          const sprite = this.add.sprite(x, y, spriteKey);
          sprite.setDisplaySize(pieceSize, pieceSize);
          sprite.setDepth(Z_DEPTHS.UI);
        }
      } else {
        // Draw captured piece with sprite and dotted outline
        const spriteKey = `w${piece.type}`;
        if (this.textures.exists(spriteKey)) {
          // Draw the piece sprite with reduced opacity
          const sprite = this.add.sprite(x, y, spriteKey);
          sprite.setDisplaySize(pieceSize, pieceSize);
          sprite.setAlpha(0.3);
          sprite.setTint(0x666666); // Gray tint
          sprite.setDepth(Z_DEPTHS.UI);

          // Draw dotted rectangle outline around the sprite
          const graphics = this.add.graphics();
          const dashLength = 4;
          const gapLength = 4;
          const halfSize = pieceSize / 2;

          graphics.lineStyle(2, 0x666666, 0.8);

          // Draw dashed rectangle by drawing dashed lines on each side
          const drawDashedLine = (x1: number, y1: number, x2: number, y2: number) => {
            const dx = x2 - x1;
            const dy = y2 - y1;
            const length = Math.sqrt(dx * dx + dy * dy);
            const segments = Math.floor(length / (dashLength + gapLength));
            const unitX = dx / length;
            const unitY = dy / length;

            for (let i = 0; i < segments; i++) {
              const startDist = i * (dashLength + gapLength);
              const endDist = startDist + dashLength;
              graphics.lineBetween(
                x1 + startDist * unitX,
                y1 + startDist * unitY,
                x1 + Math.min(endDist, length) * unitX,
                y1 + Math.min(endDist, length) * unitY
              );
            }
          };

          // Top, Right, Bottom, Left
          drawDashedLine(x - halfSize, y - halfSize, x + halfSize, y - halfSize);
          drawDashedLine(x + halfSize, y - halfSize, x + halfSize, y + halfSize);
          drawDashedLine(x + halfSize, y + halfSize, x - halfSize, y + halfSize);
          drawDashedLine(x - halfSize, y + halfSize, x - halfSize, y - halfSize);

          graphics.setDepth(Z_DEPTHS.UI + 1);
        }
      }
    });
  }

  private displayActiveRelics(): void {
    const activeUpgrades = this.gameStateManager.getActiveUpgrades();
    if (activeUpgrades.length === 0) return;

    // Position the bar in the empty band just below the board, scaled to floor
    // board size, so it never overlaps pieces.
    const floor = this.floorData.floorNumber;
    const boardBottom = this.getBoardStartY() + (floor <= 7 ? 6 : floor <= 11 ? 7 : 8) * BOARD_CONFIG.CELL_SIZE;
    const barY = boardBottom + 30;

    const title = this.add.text(GAME_CONFIG.WIDTH / 2, barY, 'YOUR UPGRADES — TAP FOR DETAILS', T.micro({ color: INK.gold }));
    title.setOrigin(0.5, 0);
    title.setLetterSpacing(1);
    title.setDepth(Z_DEPTHS.UI);

    // Lay chips out in a centered wrapping grid
    const chipH = 34;
    const gapX = 8;
    const gapY = 8;
    const maxRowWidth = GAME_CONFIG.WIDTH - 40;
    const rowStartY = barY + 28;

    const measure = (name: string) => Math.min(200, 24 + name.length * 8);

    // Group into rows
    const rows: Array<Array<{ u: any; w: number }>> = [[]];
    let rowWidth = 0;
    for (const u of activeUpgrades) {
      const w = measure(u.name);
      if (rowWidth + w + gapX > maxRowWidth && rows[rows.length - 1].length > 0) {
        rows.push([]);
        rowWidth = 0;
      }
      rows[rows.length - 1].push({ u, w });
      rowWidth += w + gapX;
    }

    rows.slice(0, 4).forEach((row, ri) => {
      const totalW = row.reduce((acc, c) => acc + c.w + gapX, -gapX);
      let x = (GAME_CONFIG.WIDTH - totalW) / 2;
      const y = rowStartY + ri * (chipH + gapY);
      for (const { u, w } of row) {
        const chipG = this.add.graphics();
        chipG.fillStyle(PALETTE.panel, 0.95);
        chipG.fillRoundedRect(x, y, w, chipH, 10);
        chipG.lineStyle(1, PALETTE.gold, 0.8);
        chipG.strokeRoundedRect(x, y, w, chipH, 10);
        chipG.setDepth(Z_DEPTHS.UI);
        const chip = this.add.rectangle(x + w / 2, y + chipH / 2, w, chipH, 0x000000, 0.001);
        chip.setInteractive({ useHandCursor: true });
        chip.setDepth(Z_DEPTHS.UI);

        const label = this.add.text(x + w / 2, y + chipH / 2, u.name, T.micro({ color: INK.primary, fontSize: '14px' }));
        label.setOrigin(0.5);
        label.setDepth(Z_DEPTHS.UI + 1);

        chip.on('pointerdown', () => {
          this.toggleUpgradeTooltip(u, x + w / 2, y - 4);
        });

        x += w + gapX;
      }
    });
  }

  private toggleUpgradeTooltip(upgrade: { name: string; description: string }, x: number, y: number): void {
    // Tapping the same chip again (or any chip) closes the open tooltip
    if (this.relicTooltip) {
      this.hideRelicTooltip();
      return;
    }
    this.showRelicTooltip(upgrade, Phaser.Math.Clamp(x, 150, GAME_CONFIG.WIDTH - 150), y);
    // Auto-dismiss after a few seconds
    this.time.delayedCall(3500, () => this.hideRelicTooltip());
  }

  private relicTooltip: Phaser.GameObjects.Container | null = null;

  private showRelicTooltip(relic: any, x: number, y: number): void {
    // Clear existing tooltip
    this.hideRelicTooltip();

    // Create tooltip container
    const tooltipBg = this.add.rectangle(0, 0, 290, 84, PALETTE.panel, 0.98);
    tooltipBg.setStrokeStyle(2, PALETTE.gold);

    const tooltipText = this.add.text(0, 0, relic.description, T.small({
      color: INK.primary, align: 'center', wordWrap: { width: 262 },
    }));
    tooltipText.setOrigin(0.5);

    this.relicTooltip = this.add.container(x, y - 50, [tooltipBg, tooltipText]);
    this.relicTooltip.setDepth(Z_DEPTHS.UI + 5);
  }

  private hideRelicTooltip(): void {
    if (this.relicTooltip) {
      this.relicTooltip.destroy();
      this.relicTooltip = null;
    }
  }


  private setupInput(): void {
    // Handle piece selection
    this.input.on('gameobjectdown', (_pointer: Phaser.Input.Pointer, gameObject: Phaser.GameObjects.Sprite) => {
      const square = gameObject.getData('square') as Square;

      if (this.isProcessingMove || !this.isPlayerTurn) {
        this.updateDebugText(`SPRITE CLICK BLOCKED: ${square} | Turn: ${this.isPlayerTurn ? 'PLAYER' : 'AI'} | Processing: ${this.isProcessingMove}`);
        return;
      }

      this.updateDebugText(`SPRITE CLICK: ${square}`);
      this.handleSquareClick(square);
    });

    // Handle board clicks (for moving to empty squares)
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      // Right-click cancels obstacle mode
      if (pointer.rightButtonDown() && this.obstacleMode !== 'none') {
        this.exitObstacleMode();
        this.showMessage('Obstacle move cancelled');
        return;
      }

      if (this.isProcessingMove || !this.isPlayerTurn) {
        this.updateDebugText(`BOARD CLICK BLOCKED | Turn: ${this.isPlayerTurn ? 'PLAYER' : 'AI'} | Processing: ${this.isProcessingMove}`);
        return;
      }

      const square = this.pixelToSquare(pointer.x, pointer.y);
      this.updateDebugText(`BOARD CLICK at (${Math.floor(pointer.x)}, ${Math.floor(pointer.y)}) -> ${square || 'NULL'}`);
      if (square) {
        this.handleSquareClick(square);
      }
    });

    // Backtick toggles debug menu
    this.input.keyboard?.on('keydown-BACKTICK', () => {
      if (this.scene.isActive(SCENES.DEBUG)) {
        this.scene.stop(SCENES.DEBUG);
      } else {
        this.scene.launch(SCENES.DEBUG);
      }
    });
  }

  private handleSquareClick(square: Square): void {
    // Handle obstacle-moving mode clicks
    if (this.obstacleMode !== 'none') {
      this.handleObstacleModeClick(square);
      return;
    }

    const piece = this.chessGame.getPieceAt(square);

    // Debug: Show what was clicked
    this.updateDebugText(`Clicked ${square} | Piece: ${piece ? piece.color + piece.type : 'NONE'} | Turn: ${this.chessGame.getTurn()}`);

    // If we have a selected piece and this is a valid move
    if (this.selectedSquare && this.validMoves.includes(square)) {
      this.executePlayerMove(this.selectedSquare, square);
      return;
    }

    // If clicking on own piece, select it
    // In extra move mode, only allow the specific piece (if set)
    if (piece && piece.color === 'w') {
      if (this.isExtraMoveActive && this.extraMovePiece && square !== this.extraMovePiece) {
        this.showMessage(`Extra move: must move piece at ${this.extraMovePiece}`);
        return;
      }
      this.selectPiece(square);
    } else {
      // Deselect if clicking elsewhere
      this.deselectPiece();

      // Debug: Why wasn't it selected?
      if (piece) {
        this.updateDebugText(`Clicked ${square} | Piece: ${piece.color}${piece.type} | NOT WHITE (color=${piece.color})`);
      }
    }
  }

  private selectPiece(square: Square): void {
    this.selectedSquare = square;
    const piece = this.chessGame.getPieceAt(square);
    const turn = this.chessGame.getTurn();
    this.validMoves = this.chessGame.getLegalMoves(square);
    this.updateHighlights();

    // Update debug text
    const debugInfo = [
      `Clicked: ${square}`,
      `Piece: ${piece ? piece.color + piece.type : 'none'}`,
      `Turn: ${turn}`,
      `Legal moves: ${this.validMoves.length} [${this.validMoves.join(', ')}]`
    ].join(' | ');
    this.updateDebugText(debugInfo);
  }

  private updateDebugText(text: string): void {
    if (this.debugText) {
      this.debugText.setText(text);
    }
  }

  /**
   * Player-facing toast message shown above the board. Fades out automatically.
   */
  private showMessage(text: string, duration: number = 2200): void {
    if (!this.messageText) {
      this.messageText = this.add.text(GAME_CONFIG.WIDTH / 2, 200, '', T.bodyStrong({
        color: INK.gold, backgroundColor: '#0a0f1cdd', padding: { x: 18, y: 10 },
        align: 'center', wordWrap: { width: GAME_CONFIG.WIDTH - 80 },
      }));
      this.messageText.setOrigin(0.5);
      this.messageText.setDepth(Z_DEPTHS.MODAL);
    }
    if (this.messageTween) {
      this.messageTween.stop();
    }
    this.messageText.setText(text);
    this.messageText.setAlpha(1);
    this.messageText.setVisible(true);
    this.messageTween = this.tweens.add({
      targets: this.messageText,
      alpha: 0,
      delay: duration,
      duration: 400,
      onComplete: () => this.messageText?.setVisible(false),
    });
  }

  /**
   * Redraw persistent board markers: last move played, the checked king,
   * sacred-ground and bounty-target squares. Called after every move.
   */
  private updateBoardMarkers(): void {
    if (!this.markerGraphics) return;
    this.markerGraphics.clear();
    this.clearMarkerLabels();
    const { CELL_SIZE } = BOARD_CONFIG;

    const fillSquare = (sq: string, color: number, alpha: number) => {
      const pos = this.squareToPixel(sq as Square);
      if (!pos) return;
      this.markerGraphics.fillStyle(color, alpha);
      this.markerGraphics.fillRect(pos.x, pos.y, CELL_SIZE, CELL_SIZE);
    };
    const ringSquare = (sq: string, color: number) => {
      const pos = this.squareToPixel(sq as Square);
      if (!pos) return;
      this.markerGraphics.lineStyle(4, color, 0.9);
      this.markerGraphics.strokeCircle(pos.x + CELL_SIZE / 2, pos.y + CELL_SIZE / 2, CELL_SIZE / 2 - 4);
    };

    // Last move (from + to)
    if (this.lastMove) {
      fillSquare(this.lastMove.from, 0xf1c40f, 0.22);
      fillSquare(this.lastMove.to, 0xf1c40f, 0.30);
    }

    // Sacred ground
    const sacred = this.chessGame.getMarkedSquare('sacred');
    if (sacred) {
      fillSquare(sacred, 0x2ecc71, 0.28);
      const pos = this.squareToPixel(sacred as Square);
      if (pos) {
        const star = this.add.text(pos.x + CELL_SIZE / 2, pos.y + CELL_SIZE / 2, '✦', {
          fontSize: `${CELL_SIZE * 0.4}px`, color: '#2ecc71',
        });
        star.setOrigin(0.5);
        star.setDepth(Z_DEPTHS.OBSTACLES + 2);
        this.markerLabels.push(star);
      }
    }

    // Bounty target
    const bounty = this.chessGame.getMarkedSquare('bounty');
    if (bounty && this.chessGame.getPieceAt(bounty as Square)) {
      ringSquare(bounty, 0xf1c40f);
      const pos = this.squareToPixel(bounty as Square);
      if (pos) {
        const coin = this.add.text(pos.x + CELL_SIZE - 8, pos.y + 8, '$', {
          fontSize: `${CELL_SIZE * 0.3}px`, color: '#f1c40f', fontStyle: 'bold',
        });
        coin.setOrigin(1, 0);
        coin.setDepth(Z_DEPTHS.PIECES + 1);
        this.markerLabels.push(coin);
      }
    }

    // King in check
    if (this.chessGame.isCheck()) {
      const checkedColor = this.chessGame.getTurn();
      const board = this.chessGame.getBoard();
      for (let row = 0; row < 8; row++) {
        for (let col = 0; col < 8; col++) {
          const piece = board[row][col];
          if (piece && piece.type === 'k' && piece.color === checkedColor) {
            const sq = String.fromCharCode(97 + col) + (8 - row);
            fillSquare(sq, 0xe74c3c, 0.45);
          }
        }
      }
    }
  }

  private markerLabels: Phaser.GameObjects.Text[] = [];

  private clearMarkerLabels(): void {
    for (const l of this.markerLabels) l.destroy();
    this.markerLabels = [];
  }

  private deselectPiece(): void {
    this.selectedSquare = null;
    this.validMoves = [];
    this.updateHighlights();
  }

  private updateHighlights(): void {
    this.highlightGraphics.clear();

    // Clear previous animated move indicators
    for (const d of this.moveFx) d.destroy();
    this.moveFx = [];

    const cell = BOARD_CONFIG.CELL_SIZE;

    if (this.selectedSquare) {
      // Selected square: soft fill + a pulsing ring
      const pos = this.squareToPixel(this.selectedSquare);
      this.highlightGraphics.fillStyle(BOARD_CONFIG.COLORS.SELECTED, 0.45);
      this.highlightGraphics.fillRect(pos.x, pos.y, cell, cell);

      const ring = this.add.rectangle(pos.x + cell / 2, pos.y + cell / 2, cell - 4, cell - 4);
      ring.setStrokeStyle(3, 0xf5b942, 0.95);
      ring.setDepth(Z_DEPTHS.HIGHLIGHTS + 1);
      this.tweens.add({ targets: ring, alpha: { from: 1, to: 0.4 }, duration: 600, yoyo: true, repeat: -1, ease: 'Sine.inOut' });
      this.moveFx.push(ring);

      // Valid moves: capture targets get a ring, quiet moves get a dot, both pop in
      for (const move of this.validMoves) {
        const mp = this.squareToPixel(move);
        const mx = mp.x + cell / 2;
        const my = mp.y + cell / 2;
        const isCapture = this.chessGame.getPieceAt(move) !== null;

        let fx: Phaser.GameObjects.GameObject;
        if (isCapture) {
          const r = this.add.circle(mx, my, cell * 0.46);
          r.setStrokeStyle(4, 0xe05a4a, 0.85);
          r.setFillStyle(0xe05a4a, 0.12);
          fx = r;
        } else {
          fx = this.add.circle(mx, my, cell * 0.16, 0x4fd18b, 0.85);
        }
        (fx as Phaser.GameObjects.Shape).setDepth(Z_DEPTHS.HIGHLIGHTS + 1);
        (fx as any).setScale(0);
        this.tweens.add({ targets: fx, scale: 1, duration: 160, ease: 'Back.out' });
        this.moveFx.push(fx);
      }
    }
  }

  private async executePlayerMove(from: Square, to: Square): Promise<void> {
    this.isProcessingMove = true;

    // Check if this move requires pawn promotion
    const requiresPromotion = this.checkPawnPromotion(from, to);

    let promotion: PieceType | undefined;
    if (requiresPromotion) {
      promotion = await this.showPromotionDialog();
      if (!promotion) {
        this.isProcessingMove = false;
        return;
      }
    }

    const move = this.chessGame.makeMove(from, to, promotion);

    if (!move || !move.isLegal) {
      console.warn('Illegal move attempted');
      this.isProcessingMove = false;
      return;
    }

    this.recordMoveForAdjudication(!!move.captured, this.pieces.get(from)?.type || 'p');
    AudioManager.getInstance().play(move.captured ? 'capture' : 'move');

    // Handle capture BEFORE animating move
    if (move.captured) {
      await this.handleCapture(to);
      this.applyPlayerCaptureUpgrades(move.captured, to);
    }

    // Animate move
    await this.animateMove(from, to);

    // Update piece position in pieces map
    const movingPiece = this.pieces.get(from);
    if (movingPiece) {
      this.pieces.delete(from);
      movingPiece.square = to;
      // Update piece type if promoted
      if (promotion) {
        movingPiece.type = promotion;
      }
      this.pieces.set(to, movingPiece);

      // Update original piece type map (move the entry from -> to)
      const originalType = this.originalPieceTypes.get(from);
      if (originalType) {
        this.originalPieceTypes.delete(from);
        this.originalPieceTypes.set(to, originalType);
      }

      // Update sprite if promoted
      if (promotion) {
        const sprite = this.pieceSprites.get(to);
        if (sprite) {
          const spriteKey = `${movingPiece.color}${promotion}`;
          sprite.setTexture(spriteKey);
        }
      }
    }

    this.deselectPiece();
    this.lastMove = { from, to };
    this.updateBoardMarkers();

    // Update fog visibility
    if (this.chessGame.isFogEnabled()) {
      this.chessGame.updateFogVisibility();
      this.updateFogOverlay();
    }

    // Chess clock: add player time bonus
    if (this.chessClock) {
      if (this.chessGame.hasUpgrade('time_bonus_10')) {
        this.chessClock.addPlayerTime(10000);
      }
      if (this.chessGame.hasUpgrade('time_bonus_30')) {
        this.chessClock.addPlayerTime(30000);
      }
    }

    // Knight Fork Bonus: +5 gold when knight attacks 2+ enemies
    if (this.chessGame.hasUpgrade('knight_fork_bonus')) {
      const pieceAtTo = this.chessGame.getPieceAt(to);
      if (pieceAtTo && pieceAtTo.type === 'n' && pieceAtTo.color === 'w') {
        const knightMoves = this.chessGame.getLegalMoves(to);
        let attackedEnemies = 0;
        for (const sq of knightMoves) {
          const target = this.chessGame.getPieceAt(sq);
          if (target && target.color === 'b') {
            attackedEnemies++;
          }
        }
        if (attackedEnemies >= 2) {
          this.gameStateManager.addGold(5);
        }
      }
    }

    if (this.chessGame.isCheck()) {
      AudioManager.getInstance().play('check');
    }

    // Pressure Play: enemy loses 3s when you give check
    if (this.chessClock && this.chessGame.hasUpgrade('pressure_play')) {
      // After player move, check if enemy is in check
      if (this.chessGame.isCheck()) {
        this.chessClock.addOpponentTime(-3000);
      }
    }

    // Check game over
    if (this.checkGameOver()) {
      return;
    }

    // Get post-move effect
    const pieceAtDest = this.chessGame.getPieceAt(to);
    const postEffect = this.chessGame.getPostMoveEffect(from, to, move.captured, pieceAtDest?.type || 'p', 'w');

    // Handle post-move effects
    if (postEffect.type === 'extra_move') {
      // Player gets another move. makeMove() already advanced the engine to the
      // opponent's turn, so switch it back to the player or no legal moves can
      // be generated for the bonus move (softlock).
      this.chessGame.switchTurn();
      if (this.checkGameOver()) return;
      this.showMessage(postEffect.message || 'Extra move!');
      this.isExtraMoveActive = true;
      this.extraMovePiece = postEffect.piece || null;
      this.isProcessingMove = false;
      // Don't switch to AI - player moves again
      return;
    }

    if (postEffect.type === 'retreat_option' && postEffect.targets && postEffect.targets.length > 0) {
      // Show retreat option - for now auto-skip (full UI would show clickable squares)
      this.showMessage(postEffect.message || 'Retreat available');
      // Continue to AI turn
    }

    // Quick Draw: first move doesn't cost a turn. Switch the engine back to the
    // player (makeMove advanced it) so the free move has legal moves.
    if (this.chessGame.isAbilityUsed('quick_draw_pending') && !this.chessGame.isAbilityUsed('quick_draw_done')) {
      this.chessGame.markAbilityUsed('quick_draw_done');
      this.chessGame.switchTurn();
      if (this.checkGameOver()) return;
      this.showMessage('Quick Draw! Free move.');
      this.isProcessingMove = false;
      return;
    }

    // Double start turns: player gets a second move at the start of battle
    if (this.chessGame.isAbilityUsed('double_start_pending') && !this.chessGame.isAbilityUsed('double_start_done')) {
      this.chessGame.markAbilityUsed('double_start_done');
      this.chessGame.switchTurn();
      if (this.checkGameOver()) return;
      this.showMessage('Double start: take another turn!');
      this.isProcessingMove = false;
      return;
    }

    // Clear stuns at start of new AI turn
    this.chessGame.clearStuns();

    // Switch to AI turn
    this.isPlayerTurn = false;
    this.isExtraMoveActive = false;
    this.extraMovePiece = null;
    this.updateTurnDisplay();

    await this.executeAITurn();
  }

  private async executeAITurn(): Promise<void> {
    this.updateTurnDisplay();

    // Small delay for better UX
    await this.delay(500);

    try {
      const aiMove = await this.chessGame.getAIMove();

      if (!aiMove || !aiMove.isLegal) {
        // The AI has no legal move under the game's actual rules (which include
        // the player's defensive upgrades). checkGameOver resolves standard
        // checkmate/stalemate; but the engine's own stalemate test ignores
        // immunity filters, so when upgrades like Pawn Shield / Royal Guard lock
        // the enemy out of every move, fall through to a player victory rather
        // than softlocking with the engine stuck on the enemy's turn.
        if (this.checkGameOver()) {
          return;
        }
        this.gameOver(true, 'The enemy is locked out — no moves left!');
        return;
      }

      // Handle capture BEFORE animating move
      if (aiMove.captured) {
        // Check if AI captured a player piece — award insurance gold
        const capturedPiece = this.pieces.get(aiMove.to);
        if (capturedPiece && capturedPiece.color === 'w' && this.chessGame.hasUpgrade('gold_on_piece_loss')) {
          const goldValues: Record<string, number> = { p: 2, n: 5, b: 5, r: 8, q: 15, k: 0 };
          const goldAmount = goldValues[capturedPiece.type] || 0;
          if (goldAmount > 0) {
            this.gameStateManager.addGold(goldAmount);
          }
        }
        await this.handleCapture(aiMove.to);
      }

      this.recordMoveForAdjudication(!!aiMove.captured, this.pieces.get(aiMove.from)?.type || 'p');
      AudioManager.getInstance().play(aiMove.captured ? 'capture' : 'move');

      // Animate AI move
      await this.animateMove(aiMove.from, aiMove.to);

      // Update piece position in pieces map
      const movingPiece = this.pieces.get(aiMove.from);
      if (movingPiece) {
        this.pieces.delete(aiMove.from);
        movingPiece.square = aiMove.to;
        // Update piece type if promoted
        if (aiMove.promotion) {
          movingPiece.type = aiMove.promotion;
        }
        this.pieces.set(aiMove.to, movingPiece);

        // Update original piece type map (move the entry from -> to)
        const originalType = this.originalPieceTypes.get(aiMove.from);
        if (originalType) {
          this.originalPieceTypes.delete(aiMove.from);
          this.originalPieceTypes.set(aiMove.to, originalType);
        }

        // Update sprite if promoted
        if (aiMove.promotion) {
          const sprite = this.pieceSprites.get(aiMove.to);
          if (sprite) {
            const spriteKey = `${movingPiece.color}${aiMove.promotion}`;
            sprite.setTexture(spriteKey);
          }
        }
      }

      this.lastMove = { from: aiMove.from, to: aiMove.to };
      this.updateBoardMarkers();

      // Check game over
      if (this.checkGameOver()) {
        return;
      }

      // Handle stun_capturer effect (if AI captured a player piece)
      if (aiMove.captured) {
        const postEffect = this.chessGame.getPostMoveEffect(aiMove.from, aiMove.to, aiMove.captured, 'p', 'b');
        if (postEffect.type === 'stun') {
          this.showStunIndicator(aiMove.to);
        }

        // Counterattack: player gets an extra turn when enemy captures (max 2 per battle)
        const counterattacksUsed =
          (this.chessGame.isAbilityUsed('counterattack_1') ? 1 : 0) +
          (this.chessGame.isAbilityUsed('counterattack_2') ? 1 : 0);
        if (this.chessGame.hasUpgrade('counterattack') && counterattacksUsed < 2) {
          this.chessGame.markAbilityUsed(counterattacksUsed === 0 ? 'counterattack_1' : 'counterattack_2');
          // Switch clock back to enemy (since player gets a free turn)
          if (this.chessClock) {
            if (this.chessGame.hasUpgrade('enemy_time_loss_10')) {
              this.chessClock.addOpponentTime(-10000);
            }
            if (!this.chessGame.isEnemyClockFrozen()) {
              this.chessClock.switchTurn();
            }
          }
          this.chessGame.incrementTurnCount();
          this.playerTurnNumber++;
          this.isPlayerTurn = true;
          this.isProcessingMove = false;
          this.updateTurnDisplay();
          this.showMessage('Counterattack! Take an extra turn.');
          return;
        }
      }

      // Chess clock: enemy time loss
      if (this.chessClock) {
        if (this.chessGame.hasUpgrade('enemy_time_loss_10')) {
          this.chessClock.addOpponentTime(-10000);
        }
        if (!this.chessGame.isEnemyClockFrozen()) {
          this.chessClock.switchTurn();
        }
      }

      // Update fog
      if (this.chessGame.isFogEnabled()) {
        this.chessGame.updateFogVisibility();
        this.updateFogOverlay();
      }

      // Increment turn count after a full player+AI cycle
      this.chessGame.incrementTurnCount();
      this.playerTurnNumber++;

      // Switch to player turn
      this.isPlayerTurn = true;
      this.isProcessingMove = false;
      this.updateTurnDisplay();

      // Show AI move preview for next turn if needed
      this.showAIMovePreviewIfNeeded();
    } catch (error) {
      console.error('AI turn error:', error);
      this.isPlayerTurn = true;
      this.isProcessingMove = false;
      this.updateTurnDisplay();
    }
  }

  private async animateMove(from: Square, to: Square): Promise<void> {
    const sprite = this.pieceSprites.get(from);
    if (!sprite) return;

    const toPos = this.squareToPixel(to);
    const targetX = toPos.x + BOARD_CONFIG.CELL_SIZE / 2;
    const targetY = toPos.y + BOARD_CONFIG.CELL_SIZE / 2;

    return new Promise((resolve) => {
      this.tweens.add({
        targets: sprite,
        x: targetX,
        y: targetY,
        duration: ANIMATION_DURATION.PIECE_MOVE,
        ease: 'Power2',
        onComplete: () => {
          // Update sprite data
          sprite.setData('square', to);

          // Update map
          this.pieceSprites.delete(from);
          this.pieceSprites.set(to, sprite);

          resolve();
        },
      });
    });
  }

  private async handleCapture(square: Square): Promise<void> {
    // Track if a player piece was captured
    const capturedPiece = this.pieces.get(square);
    if (capturedPiece && capturedPiece.color === 'w') {
      // Add to captured pieces list in game state
      const gameState = GameStateManager.getInstance().getGameState();
      gameState.capturedPieces.push(capturedPiece.type);
    }

    // Standard chess capture - piece is immediately removed
    await this.destroyPiece(square);
  }

  private async destroyPiece(square: Square): Promise<void> {
    const sprite = this.pieceSprites.get(square);

    if (sprite) {
      const piece = this.pieces.get(square);
      this.captureBurst(square, piece?.color === 'b');
      return new Promise((resolve) => {
        this.tweens.add({
          targets: sprite,
          alpha: 0,
          scale: sprite.scale * 1.4,
          angle: piece?.color === 'b' ? 25 : -25,
          duration: ANIMATION_DURATION.PIECE_CAPTURE,
          ease: 'Back.in',
          onComplete: () => {
            sprite.destroy();
            this.pieceSprites.delete(square);
            this.pieces.delete(square);
            resolve();
          },
        });
      });
    }
  }

  /** One-time tiny white-dot texture used for capture particle bursts. */
  private ensureSparkTexture(): void {
    if (this.textures.exists('spark')) return;
    const g = this.add.graphics();
    g.fillStyle(0xffffff, 1);
    g.fillCircle(6, 6, 6);
    g.generateTexture('spark', 12, 12);
    g.destroy();
  }

  /**
   * Particle burst + subtle screen shake when a piece is captured. Enemy
   * captures burst gold (your gain); your losses burst red.
   */
  private captureBurst(square: Square, enemyCaptured: boolean): void {
    const pos = this.squareToPixel(square);
    const cx = pos.x + BOARD_CONFIG.CELL_SIZE / 2;
    const cy = pos.y + BOARD_CONFIG.CELL_SIZE / 2;
    const tint = enemyCaptured ? [0xf5b942, 0xffe9a8, 0xffffff] : [0xe05a4a, 0xff9a8c, 0xffffff];

    const emitter = this.add.particles(cx, cy, 'spark', {
      speed: { min: 90, max: 240 },
      angle: { min: 0, max: 360 },
      scale: { start: 0.9, end: 0 },
      alpha: { start: 1, end: 0 },
      lifespan: { min: 250, max: 480 },
      quantity: 14,
      tint,
      blendMode: 'ADD',
      emitting: false,
    });
    emitter.setDepth(Z_DEPTHS.PIECES + 5);
    emitter.explode(14);
    this.time.delayedCall(550, () => emitter.destroy());

    // A small, snappy shake + haptic tap
    this.cameras.main.shake(110, 0.005);
    AudioManager.getInstance().haptic(enemyCaptured ? 18 : 30);
  }

  /**
   * Track a completed move for endless-game adjudication.
   * Quiet halfmoves (no capture, no pawn move) and repeated positions
   * eventually force the battle to be decided by material.
   */
  private recordMoveForAdjudication(captured: boolean, movedPieceType: PieceType): void {
    if (captured || movedPieceType === 'p') {
      this.quietHalfmoves = 0;
      this.positionCounts.clear();
    } else {
      this.quietHalfmoves++;
    }
    const key = this.getPositionKey();
    this.positionCounts.set(key, (this.positionCounts.get(key) || 0) + 1);
  }

  private getPositionKey(): string {
    const board = this.chessGame.getBoard();
    let key = this.chessGame.getTurn();
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        const piece = board[row][col];
        key += piece ? piece.color + piece.type : '.';
      }
    }
    return key;
  }

  /** Sum of standard material values (kings excluded) */
  private countMaterial(): { player: number; enemy: number } {
    const values: Record<string, number> = { p: 1, n: 3, b: 3, r: 5, q: 9, k: 0 };
    const board = this.chessGame.getBoard();
    let player = 0;
    let enemy = 0;
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        const piece = board[row][col];
        if (!piece) continue;
        if (piece.color === 'w') player += values[piece.type] || 0;
        else enemy += values[piece.type] || 0;
      }
    }
    return { player, enemy };
  }

  private checkGameOver(): boolean {
    const board = this.chessGame.getBoard();
    const playerPieces: string[] = [];
    const opponentPieces: string[] = [];

    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        const piece = board[row][col];
        if (piece) {
          if (piece.color === 'w') {
            playerPieces.push(piece.type);
          } else {
            opponentPieces.push(piece.type);
          }
        }
      }
    }

    const playerHasOtherPieces = playerPieces.some(type => type !== 'k');
    const opponentHasOtherPieces = opponentPieces.some(type => type !== 'k');

    // Enemy army routed (only their king remains): victory.
    // Without this rule some battles are literally unwinnable (e.g. K+N vs lone K
    // cannot deliver checkmate) and would loop forever.
    if (!opponentHasOtherPieces) {
      this.gameOver(true, 'Enemy army routed!');
      return true;
    }

    // Player reduced to a lone king while the enemy still has forces: defeat.
    if (!playerHasOtherPieces) {
      this.gameOver(false, 'Your army has fallen...');
      return true;
    }

    // Checkmate: the side to move is the side that has been mated.
    if (this.chessGame.isCheckmate()) {
      const playerWon = this.chessGame.getTurn() === 'b';
      this.gameOver(playerWon, 'Checkmate!');
      return true;
    }

    // Stalemate: whoever cannot move loses the battle. If the AI is frozen
    // with no legal reply, that is a win for the player - not a loss.
    if (this.chessGame.isStalemate()) {
      const playerWon = this.chessGame.getTurn() === 'b';
      this.gameOver(playerWon, playerWon ? 'Enemy paralyzed - no legal moves!' : 'No legal moves remain...');
      return true;
    }

    // Endless-game guard: 60 quiet halfmoves or threefold repetition adjudicates
    // the battle by remaining material.
    const repeated = (this.positionCounts.get(this.getPositionKey()) || 0) >= 3;
    if (this.quietHalfmoves >= 60 || repeated) {
      const { player, enemy } = this.countMaterial();
      const playerWon = player > enemy;
      this.gameOver(
        playerWon,
        playerWon ? 'Battle adjudicated - your forces prevail!' : 'Battle adjudicated - the enemy holds the field.'
      );
      return true;
    }

    return false;
  }

  /** Celebratory confetti raining from the top on victory. */
  private victoryConfetti(): void {
    this.ensureSparkTexture();
    const colors = [0xf5b942, 0x4fd18b, 0x5bb0ef, 0xffffff, 0xe05a4a];
    const emitter = this.add.particles(0, -20, 'spark', {
      x: { min: 0, max: GAME_CONFIG.WIDTH },
      y: -20,
      speedY: { min: 180, max: 360 },
      speedX: { min: -60, max: 60 },
      scale: { start: 1.1, end: 0.2 },
      alpha: { start: 1, end: 0.3 },
      lifespan: 2200,
      quantity: 4,
      frequency: 40,
      tint: colors,
      rotate: { min: 0, max: 360 },
    });
    emitter.setDepth(Z_DEPTHS.MODAL - 2);
    this.time.delayedCall(1400, () => emitter.stop());
    this.time.delayedCall(3800, () => emitter.destroy());
  }

  private gameOver(playerWon: boolean, reason?: string): void {
    this.isProcessingMove = true;
    AudioManager.getInstance().play(playerWon ? 'victory' : 'defeat');

    // Stop the clock so nobody times out on the result screen
    if (this.chessClock) {
      this.chessClock.pause();
    }

    const message = playerWon ? 'VICTORY' : 'DEFEAT';
    const color = playerWon ? INK.green : INK.red;

    AudioManager.getInstance().haptic(playerWon ? [0, 35, 45, 90] : [0, 140]);
    if (playerWon) this.victoryConfetti();

    const banner = this.add.rectangle(GAME_CONFIG.WIDTH / 2, GAME_CONFIG.HEIGHT / 2, GAME_CONFIG.WIDTH, 260, 0x0a0f1c, 0.78);
    banner.setDepth(Z_DEPTHS.MODAL - 1);

    const text = this.add.text(GAME_CONFIG.WIDTH / 2, GAME_CONFIG.HEIGHT / 2 - 20, message, T.display({ color }));
    text.setOrigin(0.5);
    text.setLetterSpacing(3);
    text.setShadow(0, 4, '#0a0f1c', 12, false, true);
    text.setDepth(Z_DEPTHS.MODAL);
    text.setScale(0.6);
    this.tweens.add({ targets: text, scale: 1, duration: 360, ease: 'Back.out' });

    if (reason) {
      const reasonText = this.add.text(GAME_CONFIG.WIDTH / 2, GAME_CONFIG.HEIGHT / 2 + 50, reason,
        T.body({ color: INK.secondary, align: 'center', wordWrap: { width: GAME_CONFIG.WIDTH - 100 } }));
      reasonText.setOrigin(0.5);
      reasonText.setDepth(Z_DEPTHS.MODAL);
    }

    if (playerWon) {
      // Save surviving WHITE pieces only (player's pieces)
      let survivingPieces = Array.from(this.pieces.values()).filter(p => p.color === 'w');

      // Revert promoted pawns back to pawns
      survivingPieces = survivingPieces.map(piece => {
        const originalType = this.originalPieceTypes.get(piece.square);
        // If this piece was originally a pawn and is now promoted, revert it
        if (originalType === 'p' && piece.type !== 'p') {
          console.log(`Reverting promoted pawn at ${piece.square} from ${piece.type} back to pawn`);
          return {
            ...piece,
            type: 'p',
          };
        }
        return piece;
      });

      this.gameStateManager.updatePlayerPieces(survivingPieces);

      // Calculate gold reward (apply per_battle and multiplier upgrades)
      const goldEarned = this.gameStateManager.calculateBattleGold(this.floorData.rewards.gold);
      this.gameStateManager.addGold(goldEarned);

      // Survivor's Pay: +2 gold per surviving piece
      if (this.chessGame.hasUpgrade('survivor_pay')) {
        const survivorGold = survivingPieces.length * 2;
        this.gameStateManager.addGold(survivorGold);
      }

      // War Profiteer: +1 gold per turn the battle lasted
      if (this.chessGame.hasUpgrade('war_profiteer')) {
        this.gameStateManager.addGold(this.chessGame.getTurnCount());
      }

      // Endgame Specialist: +30 gold if all pieces survived
      if (this.chessGame.hasUpgrade('endgame_specialist')) {
        const gameState = this.gameStateManager.getGameState();
        if (gameState.capturedPieces.length === 0) {
          this.gameStateManager.addGold(30);
        }
      }

      // Speed Bonus: +20 gold for winning in under 15 moves
      if (this.chessGame.hasUpgrade('speed_bonus')) {
        if (this.chessGame.getTurnCount() < 15) {
          this.gameStateManager.addGold(20);
        }
      }

      this.gameStateManager.recordBattleWon();

      // Beating the floor-12 boss completes the run
      if (this.floorData.type === 'boss') {
        this.gameStateManager.saveGame();
        this.time.delayedCall(2000, () => {
          this.scene.start(SCENES.RUN_SUMMARY, { victory: true });
        });
      } else {
        // Go to reward scene to choose an upgrade
        this.gameStateManager.saveGame();
        this.time.delayedCall(2000, () => {
          this.scene.start(SCENES.REWARD, { floorData: this.floorData });
        });
      }
    } else {
      // Player lost - show run summary (which ends the run)
      this.time.delayedCall(2500, () => {
        this.scene.start(SCENES.RUN_SUMMARY, { victory: false });
      });
    }
  }

  private updateTurnDisplay(): void {
    this.tweens.killTweensOf(this.turnText);
    this.turnText.setAlpha(1);
    this.turnText.setScale(1);
    if (this.isPlayerTurn) {
      this.turnText.setText('YOUR TURN');
      this.turnText.setColor(INK.green);
      this.turnText.setScale(0.85);
      this.tweens.add({ targets: this.turnText, scale: 1, duration: 220, ease: 'Back.out' });
    } else {
      this.turnText.setText('ENEMY THINKING…');
      this.turnText.setColor(INK.red);
      this.tweens.add({ targets: this.turnText, alpha: { from: 1, to: 0.5 }, duration: 520, yoyo: true, repeat: -1, ease: 'Sine.inOut' });
    }
  }

  private checkPawnPromotion(from: Square, to: Square): boolean {
    const piece = this.chessGame.getPieceAt(from);
    if (!piece || piece.type !== 'p') return false;

    const toRank = parseInt(to[1]);
    const floor = this.floorData.floorNumber;

    // Determine promotion ranks based on board size
    let whitePromotionRank: number;
    let blackPromotionRank: number;

    if (floor <= 7) {
      // 6x6: ranks 2-7
      whitePromotionRank = 7;
      blackPromotionRank = 2;
    } else if (floor <= 11) {
      // 7x7: ranks 2-8
      whitePromotionRank = 8;
      blackPromotionRank = 2;
    } else {
      // 8x8: ranks 1-8
      whitePromotionRank = 8;
      blackPromotionRank = 1;
    }

    return (piece.color === 'w' && toRank === whitePromotionRank) ||
           (piece.color === 'b' && toRank === blackPromotionRank);
  }

  private showPromotionDialog(): Promise<PieceType | undefined> {
    return new Promise((resolve) => {
      // Create semi-transparent background
      const overlay = this.add.rectangle(
        GAME_CONFIG.WIDTH / 2, GAME_CONFIG.HEIGHT / 2,
        GAME_CONFIG.WIDTH, GAME_CONFIG.HEIGHT, 0x0a0f1c, 0.78
      );
      overlay.setDepth(Z_DEPTHS.MODAL);

      // Dialog panel (rounded)
      const dialogBg = this.add.graphics();
      dialogBg.fillStyle(PALETTE.panel, 1);
      dialogBg.fillRoundedRect(GAME_CONFIG.WIDTH / 2 - 220, GAME_CONFIG.HEIGHT / 2 - 150, 440, 300, 24);
      dialogBg.lineStyle(3, PALETTE.gold, 1);
      dialogBg.strokeRoundedRect(GAME_CONFIG.WIDTH / 2 - 220, GAME_CONFIG.HEIGHT / 2 - 150, 440, 300, 24);
      dialogBg.setDepth(Z_DEPTHS.MODAL + 1);

      const title = this.add.text(GAME_CONFIG.WIDTH / 2, GAME_CONFIG.HEIGHT / 2 - 100,
        'PROMOTE TO', T.subheading({ color: INK.gold }));
      title.setOrigin(0.5);
      title.setLetterSpacing(2);
      title.setDepth(Z_DEPTHS.MODAL + 2);

      // Promotion options
      const options: { type: PieceType; name: string }[] = [
        { type: 'q', name: 'Queen' },
        { type: 'r', name: 'Rook' },
        { type: 'b', name: 'Bishop' },
        { type: 'n', name: 'Knight' },
      ];

      const buttons: Phaser.GameObjects.Container[] = [];
      const buttonY = GAME_CONFIG.HEIGHT / 2 - 20;
      const buttonSpacing = 90;
      const startX = GAME_CONFIG.WIDTH / 2 - (buttonSpacing * 1.5);

      for (let i = 0; i < options.length; i++) {
        const option = options[i];
        const x = startX + i * buttonSpacing;

        // Button background
        const buttonBg = this.add.rectangle(0, 0, 78, 78, PALETTE.panelRaised, 1);
        buttonBg.setStrokeStyle(2, PALETTE.stroke);

        // Piece sprite
        const sprite = this.add.sprite(0, 0, `w${option.type}`);
        sprite.setDisplaySize(50, 50);

        const button = this.add.container(x, buttonY, [buttonBg, sprite]);
        button.setSize(70, 70);
        button.setDepth(Z_DEPTHS.MODAL + 2);
        button.setInteractive({ useHandCursor: true });

        // Hover effects
        button.on('pointerover', () => {
          buttonBg.setFillStyle(PALETTE.blue, 1);
        });

        button.on('pointerout', () => {
          buttonBg.setFillStyle(0x34495e, 1);
        });

        // Click handler
        button.on('pointerdown', () => {
          // Clean up dialog
          overlay.destroy();
          dialogBg.destroy();
          title.destroy();
          buttons.forEach(b => b.destroy());

          resolve(option.type);
        });

        buttons.push(button);
      }
    });
  }

  // Helper methods
  private squareToPixel(square: Square): { x: number; y: number } {
    const col = square.charCodeAt(0) - 97; // 'a' = 0
    const row = 8 - parseInt(square[1]); // '8' = 0

    const { boardSize, startCol, startRow } = this.getBoardDisplayConfig();

    const startX = (GAME_CONFIG.WIDTH - BOARD_CONFIG.CELL_SIZE * boardSize) / 2;
    const startY = this.getBoardStartY();

    // Calculate position relative to restricted board
    const relativeCol = col - startCol;
    const relativeRow = row - startRow;

    return {
      x: startX + relativeCol * BOARD_CONFIG.CELL_SIZE,
      y: startY + relativeRow * BOARD_CONFIG.CELL_SIZE,
    };
  }

  private pixelToSquare(x: number, y: number): Square | null {
    const { boardSize, startCol, startRow } = this.getBoardDisplayConfig();

    const startX = (GAME_CONFIG.WIDTH - BOARD_CONFIG.CELL_SIZE * boardSize) / 2;
    const startY = this.getBoardStartY();

    const relativeCol = Math.floor((x - startX) / BOARD_CONFIG.CELL_SIZE);
    const relativeRow = Math.floor((y - startY) / BOARD_CONFIG.CELL_SIZE);

    // Check bounds relative to restricted board
    if (relativeCol < 0 || relativeCol >= boardSize ||
        relativeRow < 0 || relativeRow >= boardSize) return null;

    // Convert back to absolute board position
    const col = startCol + relativeCol;
    const row = startRow + relativeRow;

    return this.indexToSquare(col, row);
  }

  private indexToSquare(col: number, row: number): Square {
    const file = String.fromCharCode(97 + col); // 0 = 'a'
    const rank = 8 - row; // 0 = '8'
    return `${file}${rank}` as Square;
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => this.time.delayedCall(ms, resolve));
  }

  // === Upgrade Battle-Start Effects ===

  private applyBattleStartUpgrades(): void {
    const upgrades = this.chessGame.getUpgradeValues();

    // place_water_1: add 1 water square on enemy half
    if (upgrades.has('place_water_1')) {
      const squares = this.chessGame.getRandomEnemyHalfSquares(1);
      for (const sq of squares) {
        this.chessGame.addWaterSquare(sq);
      }
      if (squares.length > 0) {
        console.log('[BattleScene] Placed water at:', squares);
      }
    }

    // place_walls_1: add 1 wall on player half
    if (upgrades.has('place_walls_1')) {
      const squares = this.chessGame.getRandomPlayerHalfSquares(1);
      const dirs = ['north', 'south', 'east', 'west'];
      for (const sq of squares) {
        const dir = dirs[Math.floor(Math.random() * dirs.length)];
        this.chessGame.addWall(sq, dir);
      }
      if (squares.length > 0) {
        console.log('[BattleScene] Placed walls at:', squares);
      }
    }

    // place_teleporter: create a personal teleporter pair (your half -> enemy half)
    if (upgrades.has('place_teleporter')) {
      const entry = this.chessGame.getRandomPlayerHalfSquares(1);
      const exit = this.chessGame.getRandomEnemyHalfSquares(1);
      if (entry.length > 0 && exit.length > 0) {
        this.chessGame.addTeleporterPair(entry[0], exit[0]);
      }
    }

    // bounty_board: mark a random enemy piece for bonus gold
    if (upgrades.has('bounty_board')) {
      const board = this.chessGame.getBoard();
      const enemySquares: string[] = [];
      for (let row = 0; row < 8; row++) {
        for (let col = 0; col < 8; col++) {
          const piece = board[row]?.[col];
          if (piece && piece.color === 'b' && piece.type !== 'k') {
            const sq = String.fromCharCode(97 + col) + (8 - row);
            enemySquares.push(sq);
          }
        }
      }
      if (enemySquares.length > 0) {
        const bountySquare = enemySquares[Math.floor(Math.random() * enemySquares.length)];
        this.chessGame.markAbilityUsed(`bounty:${bountySquare}`);
        console.log(`[BattleScene] Bounty placed on enemy at: ${bountySquare}`);
      }
    }

    // sacred_ground: mark a random square as protected
    if (upgrades.has('sacred_ground')) {
      const validSquares = Array.from(this.chessGame.getValidSquares());
      if (validSquares.length > 0) {
        const sacredSquare = validSquares[Math.floor(Math.random() * validSquares.length)];
        this.chessGame.markAbilityUsed(`sacred:${sacredSquare}`);
        console.log(`[BattleScene] Sacred ground placed at: ${sacredSquare}`);
      }
    }

    // quick_draw: first move doesn't cost a turn
    if (upgrades.has('quick_draw')) {
      this.chessGame.markAbilityUsed('quick_draw_pending');
    }

    // double_start_turns: player takes 2 turns at the start
    if (upgrades.has('double_start_turns')) {
      // The first turn happens normally; we just skip AI's first turn
      this.chessGame.markAbilityUsed('double_start_pending');
    }
  }

  // === Chess Clock ===

  private setupChessClock(floor: number): void {
    const upgrades = this.chessGame.getUpgradeValues();

    // Chess clock activates on all floors
    const clockActive = floor >= CHESS_CLOCK_CONFIG.FLOOR_THRESHOLD || this.encounter?.chessClock;

    if (!clockActive || upgrades.has('unlimited_time')) return;

    // Scale time based on floor tier
    const { FLOOR_TIME } = CHESS_CLOCK_CONFIG;
    let timeMs: number;
    if (floor >= 12) {
      timeMs = FLOOR_TIME.BOSS;
    } else if (floor >= 8) {
      timeMs = FLOOR_TIME.LATE;
    } else if (floor >= 4) {
      timeMs = FLOOR_TIME.MID;
    } else {
      timeMs = FLOOR_TIME.EARLY;
    }
    this.chessClock = new ChessClock(timeMs, true);

    this.chessClock.setOnPlayerTimeout(() => {
      this.gameOver(false);
    });
    this.chessClock.setOnOpponentTimeout(() => {
      this.gameOver(true);
    });
    this.chessClock.setOnTick((data) => {
      if (this.clockText) {
        this.clockText.setText(`You ${ChessClock.formatTime(data.playerTimeMs)}`);
        this.clockText.setColor(data.playerTimeMs < 30000 ? INK.red : INK.primary);
      }
      if (this.enemyClockText) {
        this.enemyClockText.setText(`Foe ${ChessClock.formatTime(data.opponentTimeMs)}`);
      }
    });

    // Create clock display (under the gold chip, right-aligned)
    this.clockText = this.add.text(GAME_CONFIG.WIDTH - 20, 64, `You ${ChessClock.formatTime(timeMs)}`, T.small({ color: INK.primary }));
    this.clockText.setOrigin(1, 0);
    this.clockText.setDepth(Z_DEPTHS.UI);

    this.enemyClockText = this.add.text(GAME_CONFIG.WIDTH - 20, 90, `Foe ${ChessClock.formatTime(timeMs)}`, T.small({ color: INK.red }));
    this.enemyClockText.setOrigin(1, 0);
    this.enemyClockText.setDepth(Z_DEPTHS.UI);

    this.chessClock.start();
  }

  // === Fog of War ===

  private setupFogOfWar(): void {
    const hasFog = this.encounter?.fogOfWar;
    if (!hasFog) return;

    this.chessGame.setFogEnabled(true);
    this.chessGame.updateFogVisibility();

    this.fogOverlay = this.add.graphics();
    this.fogOverlay.setDepth(Z_DEPTHS.PIECES + 1);
    this.updateFogOverlay();
  }

  private updateFogOverlay(): void {
    if (!this.fogOverlay || !this.chessGame.isFogEnabled()) return;

    this.fogOverlay.clear();
    const visibleSquares = this.chessGame.getVisibleSquares();
    const { boardSize, startCol, startRow } = this.getBoardDisplayConfig();
    const { CELL_SIZE } = BOARD_CONFIG;
    const startX = (GAME_CONFIG.WIDTH - CELL_SIZE * boardSize) / 2;
    const startY = this.getBoardStartY();

    // Draw dark overlay on non-visible squares
    for (let row = 0; row < boardSize; row++) {
      for (let col = 0; col < boardSize; col++) {
        const actualCol = startCol + col;
        const actualRow = startRow + row;
        const square = this.indexToSquare(actualCol, actualRow);

        if (!visibleSquares.has(square)) {
          const x = startX + col * CELL_SIZE;
          const y = startY + row * CELL_SIZE;
          this.fogOverlay.fillStyle(0x000000, 0.8);
          this.fogOverlay.fillRect(x, y, CELL_SIZE, CELL_SIZE);
        }
      }
    }
  }

  // === Teleporter Rendering ===

  private renderTeleporters(): void {
    const pairs = this.chessGame.getTeleporterPairs();
    if (pairs.size === 0) return;

    const { boardSize, startCol, startRow } = this.getBoardDisplayConfig();
    const { CELL_SIZE } = BOARD_CONFIG;
    const startX = (GAME_CONFIG.WIDTH - CELL_SIZE * boardSize) / 2;
    const startY = this.getBoardStartY();

    for (const [entry, exit] of pairs) {
      for (const sq of [entry, exit]) {
        const col = sq.charCodeAt(0) - 97;
        const row = 8 - parseInt(sq[1]);
        const relCol = col - startCol;
        const relRow = row - startRow;

        if (relCol < 0 || relCol >= boardSize || relRow < 0 || relRow >= boardSize) continue;

        const x = startX + relCol * CELL_SIZE;
        const y = startY + relRow * CELL_SIZE;

        const g = this.add.graphics();
        g.fillStyle(0x9b59b6, 0.4);
        g.fillCircle(x + CELL_SIZE / 2, y + CELL_SIZE / 2, CELL_SIZE / 3);
        g.lineStyle(2, 0x9b59b6, 0.8);
        g.strokeCircle(x + CELL_SIZE / 2, y + CELL_SIZE / 2, CELL_SIZE / 3);
        g.setDepth(Z_DEPTHS.BOARD + 1);
        this.teleporterGraphics.push(g);

        const label = this.add.text(x + CELL_SIZE / 2, y + CELL_SIZE / 2, '⊕', {
          fontSize: `${CELL_SIZE * 0.4}px`, color: '#9b59b6'
        });
        label.setOrigin(0.5);
        label.setDepth(Z_DEPTHS.BOARD + 2);
      }
    }
  }

  // === Stun Indicator ===

  private showStunIndicator(square: Square): void {
    const pos = this.squareToPixel(square);
    const text = this.add.text(pos.x + BOARD_CONFIG.CELL_SIZE / 2, pos.y + BOARD_CONFIG.CELL_SIZE / 2, '⚡', {
      fontSize: '24px', color: '#ffff00'
    });
    text.setOrigin(0.5);
    text.setDepth(Z_DEPTHS.PIECES + 2);
    this.stunIndicators.set(square, text);

    // Remove after 1 turn
    this.time.delayedCall(2000, () => {
      text.destroy();
      this.stunIndicators.delete(square);
    });
  }

  // === AI Move Preview ===

  private async showAIMovePreviewIfNeeded(): Promise<void> {
    // AI move preview upgrade has been removed
  }

  // === Ability Buttons ===

  private createAbilityButtons(): void {
    const upgrades = this.chessGame.getUpgradeValues();
    const abilities: Array<{ label: string; key: string; action: () => void; repeatable?: boolean }> = [];

    if (upgrades.has('break_wall_once')) {
      abilities.push({
        label: '🔨 Break Wall',
        key: 'break_wall_once',
        action: () => this.activateBreakWall(),
      });
    }

    if (upgrades.has('move_water')) {
      abilities.push({
        label: '🌊 Move Water',
        key: 'move_water',
        action: () => this.activateMoveWater(),
      });
    }

    if (upgrades.has('wall_control')) {
      abilities.push({
        label: '🧱 Wall Control',
        key: 'wall_control',
        action: () => this.activateWallControl(),
      });
    }

    if (upgrades.has('turn_move_wall')) {
      abilities.push({
        label: '🧱 Move Wall',
        key: 'turn_move_wall',
        action: () => this.activateTurnMoveWall(),
        repeatable: true,
      });
    }

    if (upgrades.has('turn_move_water')) {
      abilities.push({
        label: '🌊 Move Water',
        key: 'turn_move_water',
        action: () => this.activateTurnMoveWater(),
        repeatable: true,
      });
    }

    if (upgrades.has('sacrifice_play')) {
      abilities.push({
        label: '💀 Sacrifice',
        key: 'sacrifice_play',
        action: () => this.activateSacrificePlay(),
      });
    }

    if (abilities.length === 0) return;

    const startY = GAME_CONFIG.HEIGHT - 160;
    const buttonWidth = 140;
    const spacing = 10;
    const totalWidth = abilities.length * buttonWidth + (abilities.length - 1) * spacing;
    let startX = (GAME_CONFIG.WIDTH - totalWidth) / 2;

    const bH = 44;
    for (const ability of abilities) {
      const bg = this.add.graphics();
      const drawBg = (fill: number, stroke: number) => {
        bg.clear();
        bg.fillStyle(fill, 0.95);
        bg.fillRoundedRect(-buttonWidth / 2, -bH / 2, buttonWidth, bH, 12);
        bg.lineStyle(2, stroke, 1);
        bg.strokeRoundedRect(-buttonWidth / 2, -bH / 2, buttonWidth, bH, 12);
      };
      drawBg(PALETTE.panelRaised, PALETTE.gold);

      const label = this.add.text(0, 0, ability.label, T.micro({ color: INK.primary, fontSize: '15px', align: 'center' }));
      label.setOrigin(0.5);

      const container = this.add.container(startX + buttonWidth / 2, startY, [bg, label]);
      container.setSize(buttonWidth, bH);
      container.setInteractive(new Phaser.Geom.Rectangle(-buttonWidth / 2, -bH / 2, buttonWidth, bH), Phaser.Geom.Rectangle.Contains);
      container.setDepth(Z_DEPTHS.UI);

      let used = false;
      container.on('pointerdown', () => {
        if (!ability.repeatable && this.chessGame.isAbilityUsed(ability.key)) {
          this.showMessage(`${ability.label} already used this battle`);
          return;
        }
        ability.action();
        if (!ability.repeatable) {
          this.chessGame.markAbilityUsed(ability.key);
          used = true;
          drawBg(0x2a3446, PALETTE.stroke);
          label.setColor(INK.faint);
        }
      });

      container.on('pointerover', () => { if (!used) drawBg(PALETTE.panelRaised, PALETTE.gold); });
      container.on('pointerout', () => { if (!used) drawBg(PALETTE.panelRaised, PALETTE.gold); });

      this.abilityButtons.push(container);
      startX += buttonWidth + spacing;
    }
  }

  private activateBreakWall(): void {
    // Remove a random wall obstacle
    const obstacleData = this.chessGame.getObstacleData();
    const wallEntries = Array.from(obstacleData.walls.entries());
    if (wallEntries.length > 0) {
      const [sq, dirs] = wallEntries[0];
      const dir = Array.from(dirs)[0];
      this.chessGame.removeWall(sq, dir);
      this.showMessage(`Wall at ${sq} (${dir}) destroyed!`);
    } else {
      // Try full-square obstacles
      const obstacles = Array.from(obstacleData.obstacles);
      if (obstacles.length > 0) {
        this.chessGame.removeObstacle(obstacles[0]);
        this.showMessage(`Obstacle at ${obstacles[0]} destroyed!`);
      }
    }
  }

  private activateMoveWater(): void {
    const waterSquares = this.chessGame.getWaterSquares();
    if (waterSquares.size === 0) {
      this.showMessage('No water to move');
      return;
    }
    // Move first water square to a random empty adjacent square
    const sq = Array.from(waterSquares)[0];
    const file = sq.charCodeAt(0) - 97;
    const rank = parseInt(sq[1]) - 1;
    const dirs = [[0, 1], [0, -1], [1, 0], [-1, 0]];
    for (const [df, dr] of dirs) {
      const newSq = String.fromCharCode(97 + file + df) + (rank + 1 + dr);
      if (this.chessGame.getValidSquares().has(newSq) && !this.chessGame.getPieceAt(newSq as Square)) {
        this.chessGame.removeWaterSquare(sq);
        this.chessGame.addWaterSquare(newSq);
        this.showMessage(`Water moved from ${sq} to ${newSq}`);
        return;
      }
    }
    this.showMessage('No valid square to move water to');
  }

  private activateWallControl(): void {
    // Place a wall on a random empty square on player half
    const squares = this.chessGame.getRandomPlayerHalfSquares(1);
    if (squares.length > 0) {
      const dirs = ['north', 'south', 'east', 'west'];
      const dir = dirs[Math.floor(Math.random() * dirs.length)];
      this.chessGame.addWall(squares[0], dir);
      this.showMessage(`Wall placed at ${squares[0]} (${dir})`);
    }
  }

  // === Capture-based upgrade rewards ===

  private applyPlayerCaptureUpgrades(capturedType: PieceType, captureSquare: Square): void {
    // Bounty Hunter: +3 gold per capture
    if (this.chessGame.hasUpgrade('bounty_hunter')) {
      this.gameStateManager.addGold(3);
    }

    // Scavenger: +1 gold per pawn capture
    if (this.chessGame.hasUpgrade('scavenger') && capturedType === 'p') {
      this.gameStateManager.addGold(1);
    }

    // Trophy Collector: +8 gold for capturing knight, bishop, or rook
    if (this.chessGame.hasUpgrade('trophy_collector') &&
        (capturedType === 'n' || capturedType === 'b' || capturedType === 'r')) {
      this.gameStateManager.addGold(8);
    }

    // Battle Momentum: +5s on clock per capture
    if (this.chessClock && this.chessGame.hasUpgrade('clock_per_capture_5')) {
      this.chessClock.addPlayerTime(5000);
    }

    // Blitz Instinct: +3s on clock per capture
    if (this.chessClock && this.chessGame.hasUpgrade('clock_per_capture_3')) {
      this.chessClock.addPlayerTime(3000);
    }

    // Bounty Board: +15 gold if capturing the bounty target
    if (this.chessGame.hasUpgrade('bounty_board') && this.chessGame.isAbilityUsed(`bounty:${captureSquare}`)) {
      this.gameStateManager.addGold(15);
    }

    // Knight Fork Bonus: +5 gold when knight attacks 2+ enemies after moving
    // (checked here since the knight just moved to captureSquare or any square)
    // Actually this should be checked after ANY knight move, not just captures.
    // We'll handle it in the post-move section instead.

    // Conscription: captured enemy pawns join your army next battle
    if (this.chessGame.hasUpgrade('conscription') && capturedType === 'p') {
      this.gameStateManager.addPiece('p');
    }

    // Conscript Officer: capturing knight/bishop/rook adds a pawn to your army
    if (this.chessGame.hasUpgrade('conscript_officer') &&
        (capturedType === 'n' || capturedType === 'b' || capturedType === 'r')) {
      this.gameStateManager.addPiece('p');
    }

    // Bloodlust: the capturing piece can't be captured until next turn
    if (this.chessGame.hasUpgrade('bloodlust')) {
      this.chessGame.markAbilityUsed(`bloodlust:${captureSquare}`);
    }
  }

  // === Turn-spending obstacle abilities ===

  private activateSacrificePlay(): void {
    if (!this.isPlayerTurn || this.isProcessingMove) return;
    if (this.chessGame.isCheck()) {
      this.showMessage('Cannot sacrifice while in check');
      return;
    }
    this.obstacleMode = 'sacrifice_select';
    this.deselectPiece();
    this.showMessage('Tap one of your pieces to sacrifice it');
    this.showCancelTargetingButton();
    // Highlight player pieces (except king)
    this.highlightGraphics.clear();
    for (const [sq, piece] of this.pieces) {
      if (piece.color === 'w' && piece.type !== 'k') {
        const pos = this.squareToPixel(sq);
        if (pos) {
          this.highlightGraphics.lineStyle(3, 0xff0000, 0.8);
          this.highlightGraphics.strokeRect(pos.x, pos.y, BOARD_CONFIG.CELL_SIZE, BOARD_CONFIG.CELL_SIZE);
        }
      }
    }
  }

  private activateTurnMoveWall(): void {
    if (!this.isPlayerTurn || this.isProcessingMove) return;
    if (this.chessGame.isCheck()) {
      this.showMessage('Cannot move walls while in check');
      return;
    }
    const walls = this.chessGame.getWalls();
    if (walls.size === 0) {
      this.showMessage('No walls on the board to move');
      return;
    }
    this.obstacleMode = 'select_wall';
    this.deselectPiece();
    this.showMessage('Tap a wall to pick it up');
    this.showCancelTargetingButton();
    this.highlightWalls();
  }

  private activateTurnMoveWater(): void {
    if (!this.isPlayerTurn || this.isProcessingMove) return;
    if (this.chessGame.isCheck()) {
      this.showMessage('Cannot move water while in check');
      return;
    }
    const water = this.chessGame.getWaterSquares();
    if (water.size === 0) {
      this.showMessage('No water on the board to move');
      return;
    }
    this.obstacleMode = 'select_water';
    this.deselectPiece();
    this.showMessage('Tap a water square to pick it up');
    this.showCancelTargetingButton();
    this.highlightWaterSquares();
  }

  private handleObstacleModeClick(square: Square): void {
    const sqStr = square as string;

    if (this.obstacleMode === 'select_wall') {
      // Check if this square has a wall
      const walls = this.chessGame.getWalls();
      const wallDirs = walls.get(sqStr);
      if (wallDirs && wallDirs.size > 0) {
        this.selectedWallSquare = sqStr;
        this.selectedWallDirection = Array.from(wallDirs)[0];
        this.obstacleMode = 'place_wall';
        this.highlightGraphics.clear();
        this.showMessage(`Wall at ${sqStr} (${this.selectedWallDirection}) selected. Click a square to place it.`);
        this.highlightValidWallPlacements();
      } else {
        this.showMessage('No wall on that square. Click a square with a wall.');
      }
    } else if (this.obstacleMode === 'place_wall') {
      // Place the wall on the new square
      if (!this.selectedWallSquare || !this.selectedWallDirection) return;
      const validSquares = this.chessGame.getValidSquares();
      if (!validSquares.has(sqStr)) {
        this.showMessage('Invalid square. Click a valid board square.');
        return;
      }
      if (sqStr === this.selectedWallSquare) {
        this.showMessage('Wall is already there. Pick a different square.');
        return;
      }
      // Move the wall
      this.chessGame.removeWall(this.selectedWallSquare, this.selectedWallDirection);
      this.chessGame.addWall(sqStr, this.selectedWallDirection);
      this.syncFloorObstacles();
      this.redrawObstacles();
      this.showMessage(`Wall moved from ${this.selectedWallSquare} to ${sqStr}`);
      this.exitObstacleMode();
      this.endTurnAfterObstacleMove();
    } else if (this.obstacleMode === 'select_water') {
      // Check if this square has water
      const water = this.chessGame.getWaterSquares();
      if (water.has(sqStr)) {
        // Go directly to placement
        this.selectedWallSquare = sqStr; // reuse field for source square
        this.obstacleMode = 'place_water';
        this.highlightGraphics.clear();
        this.showMessage(`Water at ${sqStr} selected. Click an empty square to place it.`);
        this.highlightValidWaterPlacements();
      } else {
        this.showMessage('No water on that square. Click a water square.');
      }
    } else if (this.obstacleMode === 'place_water') {
      if (!this.selectedWallSquare) return;
      const validSquares = this.chessGame.getValidSquares();
      if (!validSquares.has(sqStr)) {
        this.showMessage('Invalid square. Click a valid board square.');
        return;
      }
      if (this.chessGame.getWaterSquares().has(sqStr)) {
        this.showMessage('Already water there. Pick a different square.');
        return;
      }
      if (this.chessGame.getPieceAt(square)) {
        this.showMessage('Cannot place water on a piece. Pick an empty square.');
        return;
      }
      // Move the water
      this.chessGame.removeWaterSquare(this.selectedWallSquare);
      this.chessGame.addWaterSquare(sqStr);
      this.syncFloorObstacles();
      this.redrawObstacles();
      this.showMessage(`Water moved from ${this.selectedWallSquare} to ${sqStr}`);
      this.exitObstacleMode();
      this.endTurnAfterObstacleMove();
    } else if (this.obstacleMode === 'sacrifice_select') {
      // Player clicked a piece to sacrifice
      const piece = this.pieces.get(square);
      if (!piece || piece.color !== 'w' || piece.type === 'k') {
        this.showMessage('Click one of your non-king pieces to sacrifice it.');
        return;
      }
      // Find all adjacent enemy pieces
      const file = square.charCodeAt(0) - 97;
      const rank = parseInt(square[1]);
      const offsets = [[-1,-1],[-1,0],[-1,1],[0,-1],[0,1],[1,-1],[1,0],[1,1]];
      const adjacentEnemies: Square[] = [];
      for (const [df, dr] of offsets) {
        const adjFile = file + df;
        const adjRank = rank + dr;
        if (adjFile < 0 || adjFile > 7 || adjRank < 1 || adjRank > 8) continue;
        const adjSq = String.fromCharCode(97 + adjFile) + adjRank;
        const adjPiece = this.chessGame.getPieceAt(adjSq as Square);
        if (adjPiece && adjPiece.color === 'b') {
          adjacentEnemies.push(adjSq as Square);
        }
      }
      if (adjacentEnemies.length === 0) {
        this.showMessage('No adjacent enemies to capture. Pick a different piece.');
        return;
      }
      // Execute: destroy sacrificed piece, capture all adjacent enemies
      // Remove from engine first
      this.chessGame.removePieceAt(square);
      this.destroyPiece(square);
      // Track as player piece loss
      const gameState = GameStateManager.getInstance().getGameState();
      gameState.capturedPieces.push(piece.type);
      for (const enemySq of adjacentEnemies) {
        this.chessGame.removePieceAt(enemySq);
        this.destroyPiece(enemySq);
      }
      this.showMessage(`Sacrificed ${piece.type} at ${square}, captured ${adjacentEnemies.length} enemies!`);
      this.exitObstacleMode();
      this.endTurnAfterObstacleMove();
    }
  }

  private exitObstacleMode(): void {
    this.obstacleMode = 'none';
    this.selectedWallSquare = null;
    this.selectedWallDirection = null;
    this.highlightGraphics.clear();
    this.hideCancelTargetingButton();
  }

  /**
   * Floating Cancel button shown during any targeting mode. Touch devices have
   * no right-click, so this is the primary way to back out.
   */
  private showCancelTargetingButton(): void {
    this.hideCancelTargetingButton();
    const x = GAME_CONFIG.WIDTH / 2;
    const y = GAME_CONFIG.HEIGHT - 230;
    const bg = this.add.rectangle(0, 0, 200, 50, 0xc0392b, 0.95);
    bg.setStrokeStyle(2, 0xffffff);
    const label = this.add.text(0, 0, '✕ Cancel', { fontSize: '20px', color: '#ffffff', fontStyle: 'bold' });
    label.setOrigin(0.5);
    const container = this.add.container(x, y, [bg, label]);
    container.setSize(200, 50);
    container.setInteractive({ useHandCursor: true });
    container.setDepth(Z_DEPTHS.MODAL);
    container.on('pointerdown', () => {
      this.exitObstacleMode();
      this.showMessage('Cancelled');
    });
    this.cancelTargetingButton = container;
  }

  private hideCancelTargetingButton(): void {
    if (this.cancelTargetingButton) {
      this.cancelTargetingButton.destroy();
      this.cancelTargetingButton = undefined;
    }
  }

  private async endTurnAfterObstacleMove(): Promise<void> {
    this.chessGame.clearStuns();
    this.isPlayerTurn = false;
    this.isExtraMoveActive = false;
    this.extraMovePiece = null;
    this.updateTurnDisplay();

    // Chess clock: add player time bonus and switch
    if (this.chessClock) {
      if (this.chessGame.hasUpgrade('time_bonus_10')) {
        this.chessClock.addPlayerTime(10000);
      }
      if (this.chessGame.hasUpgrade('time_bonus_30')) {
        this.chessClock.addPlayerTime(30000);
      }
      if (!this.chessGame.isEnemyClockFrozen()) {
        this.chessClock.switchTurn();
      }
    }

    // Switch turn in the chess engine (white -> black)
    this.chessGame.switchTurn();
    await this.executeAITurn();
  }

  private highlightWalls(): void {
    this.highlightGraphics.clear();
    const walls = this.chessGame.getWalls();
    for (const [sq] of walls) {
      const pos = this.squareToPixel(sq as Square);
      if (pos) {
        this.highlightGraphics.lineStyle(3, 0xf1c40f, 0.8);
        this.highlightGraphics.strokeRect(
          pos.x, pos.y, BOARD_CONFIG.CELL_SIZE, BOARD_CONFIG.CELL_SIZE
        );
      }
    }
  }

  private highlightWaterSquares(): void {
    this.highlightGraphics.clear();
    const water = this.chessGame.getWaterSquares();
    for (const sq of water) {
      const pos = this.squareToPixel(sq as Square);
      if (pos) {
        this.highlightGraphics.lineStyle(3, 0xf1c40f, 0.8);
        this.highlightGraphics.strokeRect(
          pos.x, pos.y, BOARD_CONFIG.CELL_SIZE, BOARD_CONFIG.CELL_SIZE
        );
      }
    }
  }

  private highlightValidWallPlacements(): void {
    this.highlightGraphics.clear();
    const validSquares = this.chessGame.getValidSquares();
    for (const sq of validSquares) {
      const pos = this.squareToPixel(sq as Square);
      if (pos) {
        this.highlightGraphics.fillStyle(0x2ecc71, 0.3);
        this.highlightGraphics.fillRect(
          pos.x, pos.y, BOARD_CONFIG.CELL_SIZE, BOARD_CONFIG.CELL_SIZE
        );
      }
    }
  }

  private highlightValidWaterPlacements(): void {
    this.highlightGraphics.clear();
    const validSquares = this.chessGame.getValidSquares();
    const water = this.chessGame.getWaterSquares();
    for (const sq of validSquares) {
      if (water.has(sq)) continue;
      if (this.chessGame.getPieceAt(sq as Square)) continue;
      const pos = this.squareToPixel(sq as Square);
      if (pos) {
        this.highlightGraphics.fillStyle(0x2ecc71, 0.3);
        this.highlightGraphics.fillRect(
          pos.x, pos.y, BOARD_CONFIG.CELL_SIZE, BOARD_CONFIG.CELL_SIZE
        );
      }
    }
  }

  private syncFloorObstacles(): void {
    // Rebuild floorData.obstacles from the chess engine's current state
    const obstacles: import('../types').Obstacle[] = [];
    const water = this.chessGame.getWaterSquares();
    for (const sq of water) {
      obstacles.push({ square: sq as Square, type: 'water' });
    }
    const walls = this.chessGame.getWalls();
    for (const [sq, dirs] of walls) {
      for (const dir of dirs) {
        obstacles.push({
          square: sq as Square,
          type: 'wall',
          blocksDirection: dir as 'north' | 'south' | 'east' | 'west',
        });
      }
    }
    // Preserve non-wall/water obstacles from original
    if (this.floorData.obstacles) {
      for (const obs of this.floorData.obstacles) {
        if (obs.type !== 'wall' && obs.type !== 'water') {
          obstacles.push(obs);
        }
      }
    }
    this.floorData.obstacles = obstacles;
  }

  private redrawObstacles(): void {
    // Destroy all tracked obstacle graphics
    for (const obj of this.obstacleGraphicsObjects) {
      obj.destroy();
    }
    this.obstacleGraphicsObjects = [];

    // Redraw from current floorData
    const { boardSize, startCol, startRow } = this.getBoardDisplayConfig();
    if (this.floorData.obstacles && this.floorData.obstacles.length > 0) {
      this.drawObstacles(boardSize, startCol, startRow);
    }
  }

  update(): void {
    // Update chess clock if active
    if (this.chessClock) {
      this.chessClock.update();
    }
  }

  shutdown(): void {
    // Clean up
    if (this.chessGame) {
      this.chessGame.destroy();
    }
    if (this.chessClock) {
      this.chessClock.destroy();
    }
    this.pieceSprites.clear();
    this.pieces.clear();
    this.stunIndicators.clear();
    this.clearMarkerLabels();
    for (const d of this.moveFx) d.destroy();
    this.moveFx = [];
  }
}

